import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { type ChangeEvent, createContext, type FormEvent, type InputHTMLAttributes, type MouseEvent, useContext } from 'react';
import { Bounce, toast, type ToastOptions } from 'react-toastify';
import * as yup from 'yup';

import { reduxPersistor } from '@/lib';

import {
	BUSINESS_DESCRIPTION_MAX,
	BUSINESS_DESCRIPTION_MIN,
	CHATWOOT_ARTICLE_LINKS,
	type COUNTRYTYPE,
	type CURRENCYCODETYPE,
	CURRENCYCONFIG,
	type CURRENCYCONFIGTYPE,
	type CURRENCYSHORTTYPE,
	type CURRENCYSYMBOLTYPE,
	FXCONVERSION,
	NARRATION_MAX,
	PINLENGTH,
	URL_MAX,
} from '../constants';
import {
	type ApiFulfilledAction,
	type AuthSettingsPayload,
	type Breadcrumb,
	type BusinessesPayload,
	type CountryProps,
	type DateRange,
	type DeviceInfo,
	type DeviceModel,
	type FileType,
	type FXCONVERSION_TYPE,
	type IApiResponse,
	type IApiResponseError,
	type IAppStatus,
	type IAuthSettingsUSDWithdrawalMeta,
	type IBank,
	type ICurrencyConfig,
	type IDropDownItem,
	type IFiatMetaProps,
	type IPageMenuItem,
	type ISettings,
	type ISettingsFXRates,
	type ITeamMembersRole,
	type ITemMemberPermission,
	type ITransactionHistory,
	type ITransactionStatus,
	type IUseFormStage,
	type IUser,
	type IUserWallet,
	type IWalletInfoOptionProps,
	type IWalletProps,
	type IWebhookLogsEventType,
	type IWebhookLogsStatusType,
	type NetworkType,
	type TokenType,
	type TransactionHistoryType,
	type UploadErrorPayload,
	type UploadQueryReturnValue,
	type windowSizeType,
} from '../types';

export const fileTypeToMimeMap: Record<FileType, string> = {
	'pdf': 'application/pdf',
	'doc': 'application/msword',
	'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
	'xls': 'application/vnd.ms-excel',
	'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
	'ppt': 'application/vnd.ms-powerpoint',
	'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
	'txt': 'text/plain',
	'csv': 'text/csv',
	'rtf': 'application/rtf',
	'odt': 'application/vnd.oasis.opendocument.text',
	'html': 'text/html',
	'md': 'text/markdown',

	'jpg': 'image/jpeg',
	'jpeg': 'image/jpeg',
	'png': 'image/png',
	'gif': 'image/gif',
	'bmp': 'image/bmp',
	'webp': 'image/webp',
	'svg': 'image/svg+xml',
	'tiff': 'image/tiff',
	'heic': 'image/heic',

	'mp3': 'audio/mpeg',
	'wav': 'audio/wav',
	'ogg': 'audio/ogg',
	'flac': 'audio/flac',
	'aac': 'audio/aac',

	'mp4': 'video/mp4',
	'mov': 'video/quicktime',
	'avi': 'video/x-msvideo',
	'mkv': 'video/x-matroska',
	'webm': 'video/webm',
	'wmv': 'video/x-ms-wmv',

	'zip': 'application/zip',
	'rar': 'application/vnd.rar',
	'7z': 'application/x-7z-compressed',
	'tar': 'application/x-tar',
	'gz': 'application/gzip',

	'exe': 'application/vnd.microsoft.portable-executable',
	'apk': 'application/vnd.android.package-archive',
	'dmg': 'application/x-apple-diskimage',
	'iso': 'application/x-iso9660-image',
};

export const ToastPosition: ToastOptions = {
	position: 'top-right',
	autoClose: 2000,
	hideProgressBar: true,
	closeOnClick: true,
	rtl: false,
	pauseOnFocusLoss: true,
	draggable: true,
	pauseOnHover: true,
	progress: undefined,
	theme: 'light',
	transition: Bounce,
};

export const getFileExtensionByMimeType = (mimeType: string): FileType | undefined => {
	for (const [ext, mime] of Object.entries(fileTypeToMimeMap)) {
		if (mime === mimeType) return ext as FileType;
	}
	return undefined;
};

const TOAST_COOLDOWN_MS = 4000;
const recentToasts = new Map<string, number>();

export const resetToastThrottle = () => recentToasts.clear();

export const withFullStop = (value: string) => {
	const trimmed = value.trimEnd();
	if (!trimmed) return trimmed;
	return /[.!?…]$/.test(trimmed) ? trimmed : `${trimmed}.`;
};

export const showToast = (type: 'success' | 'error', message: string) => {
	const text = withFullStop(capitalizeFirstLetter(message));
	const toastId = `${type}:${text}`;

	const now = Date.now();
	const lastShown = recentToasts.get(toastId);
	if (toast.isActive(toastId) || (lastShown !== undefined && now - lastShown < TOAST_COOLDOWN_MS)) return;
	recentToasts.set(toastId, now);

	const options: ToastOptions = { ...ToastPosition, toastId };

	switch (type) {
		case 'success':
			toast.success(text, options);
			break;
		case 'error':
			toast.error(text, options);
			break;
		default:
			throw new Error(`Invalid toast type: ${type as string}`);
	}
};

export const getLocationFrom = () => {
	const { pathname } = window.location;
	const searchParams = window.location.search;
	return encodeURIComponent(`${pathname}${searchParams}`);
};

export * from './kyb';

export const strictEmailValidation = (email: string): boolean => {
	const emailRegex =
		/^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

	if (!emailRegex.test(email)) return false;

	const [localPart, domain] = email.split('@');

	if (localPart.length > 64) return false;
	if (localPart.startsWith('.') || localPart.endsWith('.')) return false;
	if (localPart.includes('..')) return false;

	if (domain.length > 253) return false;
	if (domain.startsWith('-') || domain.endsWith('-')) return false;
	if (domain.includes('..')) return false;

	const tldParts = domain.split('.');
	const tld = tldParts[tldParts.length - 1];
	if (tld.length < 2) return false;

	return true;
};

export const emailValidation = yup
	.string()
	.required('Email is required')
	.trim()
	.lowercase()
	.min(3, 'Email must be at least 3 characters')
	.max(254, 'Email must not exceed 254 characters')
	.matches(
		/^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/,
		'Please enter a valid email address'
	);

const DISPOSABLE_EMAIL_DOMAINS = new Set<string>([
	'fishnone.com',
	'0-mail.com',
	'01022.hk',
	'01130.hk',
	'027168.com',
	'062e.com',
	'0815.ru',
	'0815.su',
	'0845.ru',
	'0box.eu',
	'0cd.cn',
	'0clickemail.com',
	'0n0ff.net',
	'0nelce.com',
	'0rg.fr',
	'0v.ro',
	'0w.ro',
	'0wnd.net',
	'0wnd.org',
	'0x207.info',
	'1-8.biz',
	'1-second-mail.site',
	'1-tm.com',
	'10-minute-mail.com',
	'1000rebates.stream',
	'100likers.com',
	'105kg.ru',
	'10dk.email',
	'10inbox.online',
	'10mail.com',
	'10mail.org',
	'10mail.tk',
	'10mail.xyz',
	'10minemail.com',
	'10minmail.de',
	'10minut.com.pl',
	'10minut.xyz',
	'10minutemail.be',
	'10minutemail.cf',
	'10minutemail.co.uk',
	'10minutemail.co.za',
	'10minutemail.com',
	'10minutemail.de',
	'10minutemail.ga',
	'10minutemail.gq',
	'10minutemail.ml',
	'10minutemail.net',
	'10minutemail.nl',
	'10minutemail.pro',
	'10minutemail.us',
	'10minutemailbox.com',
	'10minutemails.in',
	'10minutenemail.de',
	'10minutenmail.xyz',
	'10minutesmail.com',
	'10minutesmail.fr',
	'10minutmail.pl',
	'10x9.com',
	'11163.com',
	'123-m.com',
	'123clone.com',
	'123mails.org',
	'12bclass.us',
	'12hosting.net',
	'12houremail.com',
	'12minutemail.com',
	'12minutemail.net',
	'12storage.com',
	'1337.care',
	'140unichars.com',
	'147.cl',
	'14club.org.uk',
	'14n.co.uk',
	'15963.fr.nf',
	'15qm.com',
	'1820mail.vip',
	'189.email',
	'191mariobet.com',
	'1blackmoon.com',
	'1ce.us',
	'1chuan.com',
	'1clck2.com',
	'1fsdfdsfsdf.tk',
	'1mail.ml',
	'1nom.org',
	'1pad.de',
	'1s.fr',
	'1sec.site',
	'1secmail.com',
	'1secmail.net',
	'1secmail.org',
	'1secmail.space',
	'1secmail.website',
	'1st-forms.com',
	'1sworld.com',
	'1to1mail.org',
	'1trick.net',
	'1usemail.com',
	'1webmail.info',
	'1xp.fr',
	'1zhuan.com',
	'2012-2016.ru',
	'20email.eu',
	'20email.it',
	'20mail.eu',
	'20mail.in',
	'20mail.it',
	'20minutemail.com',
	'20minutemail.it',
	'20minutesmail.com',
	'20mm.eu',
	'2120001.net',
	'21cn.com',
	'2200freefonts.com',
	'247web.net',
	'24faw.com',
	'24hinbox.com',
	'24hourmail.com',
	'24hourmail.net',
	'24mail.top',
	'25u.com',
	'2anom.com',
	'2chmail.net',
	'2ether.net',
	'2fdgdfgdfgdf.tk',
	'2odem.com',
	'2prong.com',
	'2wc.info',
	'300book.info',
	'30mail.ir',
	'30minutemail.com',
	'30wave.com',
	'3202.com',
	'36ru.com',
	'3a88.dev',
	'3d-painting.com',
	'3dkai.com',
	'3fdn.com',
	'3l6.com',
	'3littlemiracles.com',
	'3mail.ga',
	'3trtretgfrfe.tk',
	'4-n.us',
	'4057.com',
	'418.dk',
	'42o.org',
	'42web.io',
	'48dz.com',
	'48hr.email',
	'4gfdsgfdgfd.tk',
	'4k5.net',
	'4mail.cf',
	'4mail.ga',
	'4nextmail.com',
	'4nmv.ru',
	'4pu.com',
	'4save.net',
	'4tb.host',
	'4warding.com',
	'4warding.net',
	'4warding.org',
	'50set.ru',
	'52mails.com',
	'55hosting.net',
	'5ghgfhfghfgh.tk',
	'5gramos.com',
	'5july.org',
	'5mail.cf',
	'5mail.ga',
	'5minutemail.net',
	'5oz.ru',
	'5semail.com',
	'5tb.in',
	'5x25.com',
	'5ymail.com',
	'60minutemail.com',
	'672643.net',
	'675hosting.com',
	'675hosting.net',
	'675hosting.org',
	'6hjgjhgkilkj.tk',
	'6ip.us',
	'6mail.cf',
	'6mail.ga',
	'6mail.ml',
	'6n9.net',
	'6paq.com',
	'6somok.ru',
	'6url.com',
	'75hosting.com',
	'75hosting.net',
	'75hosting.org',
	'7days-printing.com',
	'7mail.ga',
	'7mail.ml',
	'7novels.com',
	'7tags.com',
	'80665.com',
	'8127ep.com',
	'816qs.com',
	'8mail.cf',
	'8mail.ga',
	'8mail.ml',
	'99.com',
	'99cows.com',
	'99experts.com',
	'9mail.cf',
	'9me.site',
	'9mot.ru',
	'9ox.net',
	'9q.ro',
	'a-bc.net',
	'a-germandu.de',
	'a45.in',
	'a7996.com',
	'aa5zy64.com',
	'aakk.link',
	'aakkmail.com',
	'aaqwe.ru',
	'aaqwe.store',
	'aard.org.uk',
	'abacuswe.us',
	'abakiss.com',
	'abatido.com',
	'abcmail.email',
	'abevw.com',
	'abilitywe.us',
	'abo-free.fr.nf',
	'abouse.space',
	'abovewe.us',
	'absolutewe.us',
	'abundantwe.us',
	'abusemail.de',
	'abuser.eu',
	'abyis.com',
	'abyssmail.com',
	'ac-malin.fr.nf',
	'ac20mail.in',
	'academiccommunity.com',
	'academywe.us',
	'acc1s.com',
	'acc1s.net',
	'accclone.com',
	'acceleratewe.us',
	'accentwe.us',
	'acceptwe.us',
	'acclaimwe.us',
	'accordwe.us',
	'accountscenter.support',
	'accreditedwe.us',
	'acedby.com',
	'achievementwe.us',
	'achievewe.us',
	'acornwe.us',
	'acrossgracealley.com',
	'acrylicwe.us',
	'actarus.infos.st',
	'activatewe.us',
	'activitywe.us',
	'acucre.com',
	'acuitywe.us',
	'acumenwe.us',
	'adaptivewe.us',
	'adaptwe.us',
	'add3000.pp.ua',
	'addictingtrailers.com',
	'addmails.com',
	'addrin.uk',
	'adeptwe.us',
	'adfskj.com',
	'adios.email',
	'adiq.eu',
	'aditus.info',
	'admiralwe.us',
	'adn3t.com',
	'ado888.biz',
	'adobeccepdm.com',
	'adoniswe.us',
	'adpugh.org',
	'adresse.infos.st',
	'adroh.com',
	'adsd.org',
	'adubiz.info',
	'adult-work.info',
	'advantagewe.us',
	'advantimo.com',
	'adventurewe.us',
	'adventwe.us',
	'advisorwe.us',
	'advitise.com',
	'advocatewe.us',
	'adwaterandstir.com',
	'aegde.com',
	'aegia.net',
	'aegiscorp.net',
	'aegiswe.us',
	'aelo.es',
	'aeonpsi.com',
	'afarek.com',
	'afeeyah.store',
	'affecting.org',
	'affiliate-nebenjob.info',
	'affiliatedwe.us',
	'affilikingz.de',
	'affinitywe.us',
	'affluentwe.us',
	'affordablewe.us',
	'afia.pro',
	'afrobacon.com',
	'aftercorporation.com',
	'afterhourswe.us',
	'afternea.sbs',
	'afw.fr.nf',
	'agedmail.com',
	'agendawe.us',
	'agger.ro',
	'agilewe.us',
	'agiuse.com',
	'agorawe.us',
	'agtx.net',
	'aheadwe.us',
	'ahem.email',
	'ahk.jp',
	'ahmedkhlef.com',
	'ahvin.com',
	'aikunkun.com',
	'ailicke.com',
	'aiphotoeditor.io',
	'aiphotoenhancer.me',
	'air2token.com',
	'airmailbox.website',
	'airsworld.net',
	'aiworldx.com',
	'aixnv.com',
	'ajaxapp.net',
	'akapost.com',
	'akbip.com',
	'akerd.com',
	'akgq701.com',
	'akirapowered.com',
	'akmail.in',
	'akugu.com',
	'akunlama.com',
	'al-qaeda.us',
	'albill.com',
	'albionwe.us',
	'alchemywe.us',
	'aleh.de',
	'alfaceti.com',
	'aliaswe.us',
	'aliban.org',
	'alienware13.com',
	'aligamel.com',
	'aligroup.uk',
	'alilot.com',
	'alina-schiesser.ch',
	'alisaol.com',
	'alisongamel.com',
	'alisree.com',
	'alivance.com',
	'alivewe.us',
	'all-cats.ru',
	'allaccesswe.us',
	'allamericanwe.us',
	'allaroundwe.us',
	'alldirectbuy.com',
	'allegiancewe.us',
	'allegrowe.us',
	'allemojikeyboard.com',
	'allfreemail.net',
	'allgoodwe.us',
	'alliancewe.us',
	'allinonewe.us',
	'allofthem.net',
	'alloutwe.us',
	'allowed.org',
	'alloywe.us',
	'allprowe.us',
	'allreview4u.com',
	'allseasonswe.us',
	'allstarwe.us',
	'allthegoodnamesaretaken.org',
	'allumhall.co.uk',
	'allurewe.us',
	'allwebemails.com',
	'almatips.com',
	'almiswelfare.org',
	'almondwe.us',
	'alosp.com',
	'alph.wtf',
	'alpha-web.net',
	'alphaomegawe.us',
	'alphax.fr.nf',
	'alpinewe.us',
	'alreval.com',
	'altairwe.us',
	'altitudewe.us',
	'altmails.com',
	'altrans.fr.nf',
	'altuswe.us',
	'aluimport.com',
	'alves.fr.nf',
	'ama-trade.de',
	'ama-trans.de',
	'amadeuswe.us',
	'amail.club',
	'amail.com',
	'amail1.com',
	'amail4.me',
	'amazingly.online',
	'amazon-aws.org',
	'amberwe.us',
	'ambiancewe.us',
	'ambitiouswe.us',
	'amelabs.com',
	'americanawe.us',
	'americancivichub.com',
	'americasbestwe.us',
	'americaswe.us',
	'amex409.monster',
	'amicuswe.us',
	'amilegit.com',
	'aminating.com',
	'amiri.net',
	'amiriindustries.com',
	'amplewe.us',
	'amplifiedwe.us',
	'amplifywe.us',
	'ampsylike.com',
	'analogwe.us',
	'analysiswe.us',
	'analyticalwe.us',
	'analyticswe.us',
	'analyticwe.us',
	'anappfor.com',
	'anappthat.com',
	'anarac.com',
	'andreihusanu.ro',
	'andthen.us',
	'animesos.com',
	'aniross.com',
	'anit.ro',
	'annd.us',
	'ano-mail.net',
	'anon-mail.de',
	'anonbox.net',
	'anonmail.top',
	'anonmails.de',
	'anonymail.dk',
	'anonymbox.com',
	'anonymized.org',
	'anonymousness.com',
	'anotherdomaincyka.tk',
	'anquandx.com',
	'ansibleemail.com',
	'anthony-junkmail.com',
	'antireg.com',
	'antireg.ru',
	'antispam.de',
	'antispam.fr.nf',
	'antispam24.de',
	'antispammail.de',
	'any.pink',
	'anyalias.com',
	'anypng.com',
	'aoeuhtns.com',
	'apfelkorps.de',
	'aphlog.com',
	'apkmd.com',
	'apocaw.com',
	'appc.se',
	'appinventor.nl',
	'appixie.com',
	'appmail.uk',
	'apps.dj',
	'appzily.com',
	'aprte.com',
	'arapps.me',
	'arcadein.com',
	'arduino.hk',
	'arenahiveai.com',
	'ares.edu.pl',
	'ariaz.jetzt',
	'armyspy.com',
	'aron.us',
	'arroisijewellery.com',
	'art-en-ligne.pro',
	'artman-conception.com',
	'arur01.tk',
	'arurgitu.gq',
	'arvato-community.de',
	'arxxwalls.com',
	'asaption.com',
	'aschenbrandt.net',
	'asciibinder.net',
	'asdasd.nl',
	'asdasd.ru',
	'ashleyandrew.com',
	'asimarif.com',
	'ask-mail.com',
	'asorent.com',
	'ass.pp.ua',
	'assurmail.net',
	'astonut.tk',
	'astroempires.info',
	'asu.mx',
	'asu.su',
	'asurad.com',
	'at.hm',
	'at0mik.org',
	'atnextmail.com',
	'attnetwork.com',
	'au1688x.us',
	'aubady.com',
	'augmentationtechnology.com',
	'ausgefallen.info',
	'auth2fa.com',
	'auti.st',
	'autorobotica.com',
	'autosouvenir39.ru',
	'autotwollow.com',
	'autowb.com',
	'autoxugiare.com',
	'autre.fr.nf',
	'averdov.com',
	'avia-tonic.fr',
	'avls.pt',
	'avtolev.com',
	'awa.pics',
	'awatum.de',
	'awdrt.org',
	'awesome47.com',
	'awesomesaucemail.org',
	'awgarstone.com',
	'awiki.org',
	'awsoo.com',
	'axiz.org',
	'axon7zte.com',
	'axsup.net',
	'ayakamail.cf',
	'azame.pw',
	'azazazatashkent.tk',
	'azcomputerworks.com',
	'azeqsd.fr.nf',
	'azmeil.tk',
	'azulejoslowcost.es',
	'azuretechtalk.net',
	'azzancoffee.com',
	'b1of96u.com',
	'b2bx.net',
	'b2cmail.de',
	'b7s.ru',
	'backilnge.com',
	'bacteroidmail.com',
	'badgerland.eu',
	'badoop.com',
	'badopsec.lol',
	'badpotato.tk',
	'balaket.com',
	'balawo.com',
	'bangban.uk',
	'banhang14.com',
	'banit.club',
	'banit.me',
	'bank-opros1.ru',
	'bankinnepal.com',
	'bareed.ws',
	'barooko.com',
	'barryogorman.com',
	'bartdevos.be',
	'basepathgrid.com',
	'basscode.org',
	'bauwerke-online.com',
	'baybabes.com',
	'bazaaboom.com',
	'bbbbyyzz.info',
	'bbhost.us',
	'bbitf.com',
	'bbitj.com',
	'bbitq.com',
	'bboys.fr.nf',
	'bcaoo.com',
	'bcast.ws',
	'bcb.ro',
	'bccto.me',
	'bcm.edu.pl',
	'bcooq.com',
	'bdgstr.com',
	'bdm.ovh',
	'bdmuzic.pw',
	'bdshar.com',
	'beaconmessenger.com',
	'bearsarefuzzy.com',
	'beastmail.space',
	'beddly.com',
	'beefmilk.com',
	'beelsil.com',
	'begemail.com',
	'belamail.org',
	'belgianairways.com',
	'belljonestax.com',
	'beluckygame.com',
	'benipaula.org',
	'benphim.com',
	'benphim.net',
	'bepureme.com',
	'beribase.ru',
	'beribaza.ru',
	'berirabotay.ru',
	'berkahfb.com',
	'berwie.com',
	'best-hosting.biz',
	'best-john-boats.com',
	'best-temp-mail.com',
	'best-vpn.xyz',
	'bestchoiceusedcar.com',
	'bestlistbase.com',
	'bestoption25.club',
	'bestparadize.com',
	'bestsoundeffects.com',
	'besttempmail.com',
	'beta.edu.pl',
	'betr.co',
	'betteryoutime.com',
	'bflcafe.com',
	'bgtmail.com',
	'bgx.ro',
	'bheps.com',
	'bhuxp.org',
	'bidourlnks.com',
	'big1.us',
	'bigprofessor.so',
	'bigstring.com',
	'bigwhoop.co.za',
	'bij.pl',
	'bin-ich.com',
	'binafex.com',
	'binbx.net',
	'binech.com',
	'binich.com',
	'binka.me',
	'binkmail.com',
	'binnary.com',
	'binsh.kro.kr',
	'bio-muesli.info',
	'bio-muesli.net',
	'biojuris.com',
	'bione.co',
	'bipochub.com',
	'bitmens.com',
	'bitwhites.top',
	'bitymails.us',
	'biz.st',
	'bizisstance.com',
	'blackgoldagency.ru',
	'blackmarket.to',
	'bladesmail.net',
	'blenched.com',
	'blendlog.com',
	'blenro.com',
	'blip.ch',
	'blip.ovh',
	'blnkt.net',
	'block521.com',
	'blogmyway.org',
	'blogos.net',
	'blogspam.ro',
	'blondemorkin.com',
	'blondmail.com',
	'bltiwd.com',
	'bluedumpling.info',
	'bluewerks.com',
	'bnote.com',
	'boatmail.us',
	'bobgf.ru',
	'bobgf.store',
	'bobmail.info',
	'bobmurchison.com',
	'boerakemail.com',
	'bofthew.com',
	'bomnet.net',
	'bonobo.email',
	'boofx.com',
	'bookgame.org',
	'bookthemmore.com',
	'boostme.es',
	'bootssl.com',
	'bootybay.de',
	'boranora.com',
	'bored.lol',
	'borged.com',
	'borged.net',
	'borged.org',
	'bot.nu',
	'botgetlink.com',
	'botgetlink.net',
	'botnet.my.id',
	'boun.cr',
	'bouncr.com',
	'box-mail.ru',
	'box-mail.store',
	'boxem.ru',
	'boxem.store',
	'boxfi.uk',
	'boxformail.in',
	'boximail.com',
	'boxlet.ru',
	'boxlet.store',
	'boxmach.com',
	'boxmail.lol',
	'boxomail.live',
	'boxtemp.com.br',
	'boyaga.com',
	'bptfp.net',
	'brand-app.biz',
	'brandallday.net',
	'brashbeat.online',
	'brasx.org',
	'breakthru.com',
	'brefmail.com',
	'brennendesreich.de',
	'briefalpha.org',
	'briggsmarcus.com',
	'broadbandninja.com',
	'bskyx.fun',
	'bsnow.net',
	'bspamfree.org',
	'bspooky.com',
	'bst-72.com',
	'btb-notes.com',
	'btc.email',
	'btcmail.pw',
	'btcmod.com',
	'btizet.pl',
	'btsblock.com',
	'buccalmassage.ru',
	'budaya-tionghoa.com',
	'budayationghoa.com',
	'buffemail.com',
	'bugfoo.com',
	'bugmenever.com',
	'bugmenot.com',
	'bukanimers.com',
	'bukhariansiddur.com',
	'bulrushpress.com',
	'bultoc.com',
	'bum.net',
	'bumail.site',
	'bumpymail.com',
	'bunchofidiots.com',
	'bund.us',
	'bundes-li.ga',
	'bunsenhoneydew.com',
	'burangir.com',
	'burnthespam.info',
	'burstmail.info',
	'business-degree.live',
	'businessbackend.com',
	'businesssuccessislifesuccess.com',
	'buspad.org',
	'bussitussi.com',
	'buy-blog.com',
	'buymoreplays.com',
	'buyordie.info',
	'buyusdomain.com',
	'buyusedlibrarybooks.org',
	'buzzcluby.com',
	'buzzcut.ws',
	'bwmyga.com',
	'byagu.com',
	'byebyemail.com',
	'byespm.com',
	'byom.de',
	'bytedigi.com',
	'c-eric.fr.nf',
	'c1ph3r.xyz',
	'c4.fr',
	'c4pro.uk',
	'c51vsgq.com',
	'caainpt.com',
	'cabiste.fr.nf',
	'cachedot.net',
	'cade.org.uk',
	'cafesui.com',
	'caftee.com',
	'calendro.fr.nf',
	'californiafitnessdeals.com',
	'calima.asso.st',
	'cam4you.cc',
	'cameltok.com',
	'camera47.net',
	'cameraity.com',
	'camping-grill.info',
	'cancer-treatment.xyz',
	'candassociates.com',
	'candymail.de',
	'cane.pw',
	'canfga.org',
	'cantozil.com',
	'canvect.com',
	'capcutku.io',
	'capcutmeflo.shop',
	'capcutpro.click',
	'capitalistdilemma.com',
	'captus.cyou',
	'car101.pro',
	'carbtc.net',
	'carpin.org',
	'cars2.club',
	'carsencyclopedia.com',
	'cartelera.org',
	'caseedu.tk',
	'cashflow35.co',
	'cashflow35.com',
	'casualdx.com',
	'catgroup.uk',
	'cavi.mx',
	'caye.org.uk',
	'cazlg.com',
	'cbair.com',
	'cbes.net',
	'cbty.ru',
	'cbty.store',
	'cckuy.com',
	'ccmail.uk',
	'ccqu.top',
	'cctoolz.com',
	'cdfaq.com',
	'cdnmia.com',
	'cdnripple.com',
	'cdpa.cc',
	'ceed.se',
	'cek.pm',
	'cellurl.com',
	'cengrop.com',
	'centermail.com',
	'centermail.net',
	'certexx.fr.nf',
	'certificenter.com',
	'certve.com',
	'cetpass.com',
	'cfo2go.ro',
	'cggup.com',
	'ch.ma',
	'chacuo.net',
	'chahcyrans.com',
	'chaichuang.com',
	'chaineor.com',
	'chalupaurybnicku.cz',
	'chamconnho.com',
	'chammy.info',
	'chansd.com',
	'chaocosen.com',
	'chapsmail.com',
	'chasefreedomactivate.com',
	'chatgptuk.pp.ua',
	'chatich.com',
	'chatworkstation.com',
	'chaublog.com',
	'cheaphub.net',
	'cheatmail.de',
	'chefalicious.com',
	'chenbot.email',
	'chessgameland.com',
	'chessgamingworld.com',
	'chewydonut.com',
	'chibakenma.ml',
	'chickenkiller.com',
	'chielo.com',
	'childsavetrust.org',
	'chilkat.com',
	'chinamkm.com',
	'chingchongme.site',
	'chithinh.com',
	'chitthi.in',
	'choco.la',
	'chogmail.com',
	'choicemail1.com',
	'choichay.com',
	'choigi.com',
	'chong-mail.com',
	'chong-mail.net',
	'chong-mail.org',
	'chophim.com',
	'chosenx.com',
	'chotgo.com',
	'chuan.info',
	'chumpstakingdumps.com',
	'cigar-auctions.com',
	'citmo.net',
	'civikli.com',
	'civx.org',
	'ckaazaza.tk',
	'cketrust.org',
	'ckiso.com',
	'ckvn.edu.vn',
	'cl-cl.org',
	'cl0ne.net',
	'claimab.com',
	'clandest.in',
	'classesmail.com',
	'claudd.com',
	'clearwatermail.info',
	'click-email.com',
	'clickdeal.co',
	'clipmail.eu',
	'clixser.com',
	'clockemail.com',
	'cloneemail.com',
	'clonemailsieure.com',
	'clonemoi.tk',
	'clonetrust.com',
	'cloud-mail.top',
	'cloud-temp.com',
	'cloudflare.gay',
	'cloudgen.world',
	'cloudsign.in',
	'clout.wiki',
	'clowmail.com',
	'clrmail.com',
	'club106.org.uk',
	'cmail.club',
	'cmail.com',
	'cmail.net',
	'cmail.org',
	'cmdkl.com',
	'cmhvzylmfc.com',
	'cnamed.com',
	'cndps.com',
	'cneemail.com',
	'cnew.ir',
	'cnieux.com',
	'cnmsg.net',
	'cnsds.de',
	'co.cc',
	'coasah.com',
	'cobal.infos.st',
	'cobarekyo1.ml',
	'cocoro.uk',
	'cocovpn.com',
	'code-gmail.com',
	'codeandscotch.com',
	'codeguard.net',
	'coderdir.com',
	'codestar.site',
	'codivide.com',
	'coffeeazzan.com',
	'coffeejadore.com',
	'coffeetimer24.com',
	'coieo.com',
	'coin-host.net',
	'coinlink.club',
	'cok.org.uk',
	'colabeta.com',
	'colaname.com',
	'coldemail.info',
	'collegewh.edu.pl',
	'colorcastmail.com',
	'colurmish.com',
	'comfythings.com',
	'compareshippingrates.org',
	'completegolfswing.com',
	'compressjpg.io',
	'comwest.de',
	'comyze.org',
	'concu.net',
	'conf.work',
	'confmin.com',
	'connho.com',
	'connho.net',
	'consumerriot.com',
	'contaco.org',
	'contact.infos.st',
	'contbay.com',
	'cood.food',
	'cooh-2.site',
	'cookie007.fr.nf',
	'coolandwacky.us',
	'coolimpool.org',
	'copyhome.win',
	'coreclip.com',
	'corhash.net',
	'cosmorph.com',
	'cosoinan.com',
	'cotasen.com',
	'cotigz.com',
	'courriel.fr.nf',
	'courrieltemporaire.com',
	'coza.ro',
	'cpc.cx',
	'crankhole.com',
	'crapmail.org',
	'crastination.de',
	'crazespaces.pw',
	'crazymailing.com',
	'cream.pink',
	'credit-loans.xyz',
	'crepeau12.com',
	'creteanu.com',
	'cringemonster.com',
	'cross-law.ga',
	'cross-law.gq',
	'cross.edu.pl',
	'crossmailjet.com',
	'crossroadsmail.com',
	'crunchcompass.com',
	'crusthost.com',
	'cryptogmail.com',
	'cs.email',
	'csh.ro',
	'cslua.com',
	'cspaus.com',
	'cszbl.com',
	'ctmailing.us',
	'ctos.ch',
	'cu.cc',
	'cubene.com',
	'cubiclink.com',
	'cucadas.com',
	'cuendita.com',
	'cuirushi.org',
	'cuoly.com',
	'cupbest.com',
	'curlhph.tk',
	'currentmail.com',
	'curryworld.de',
	'cust.in',
	'cutefrogs.xyz',
	'cutout.club',
	'cutradition.com',
	'cutsup.com',
	'cuvox.de',
	'cwetg.co.uk',
	'cxwet.com',
	'cyber-innovation.club',
	'cyber-phone.eu',
	'cycinst.com',
	'cyclelove.cc',
	'cylab.org',
	'cypi.fr',
	'd1yun.com',
	'd3p.dk',
	'daabox.com',
	'dab.ro',
	'dacoolest.com',
	'daemsteam.com',
	'daerdy.com',
	'daibond.info',
	'daily-email.com',
	'daintly.com',
	'dakaka.org',
	'damai.webcam',
	'dammexe.net',
	'damnthespam.com',
	'dandikmail.com',
	'dann.mywire.org',
	'dao.pp.ua',
	'daouse.com',
	'dapurx.me',
	'darkharvestfilms.com',
	'daryxfox.net',
	'dasdasdascyka.tk',
	'dash-pads.com',
	'dataarca.com',
	'datadudi.com',
	'datarca.com',
	'datazo.ca',
	'datenschutz.ru',
	'datum2.com',
	'davidkoh.net',
	'davidlcreative.com',
	'dawin.com',
	'daymail.life',
	'daymailonline.com',
	'dayrep.com',
	'dbmail.one',
	'dboso.com',
	'dbunker.com',
	'dcctb.com',
	'dcemail.com',
	'dcpa.net',
	'ddcrew.com',
	'de-a.org',
	'dea-21olympic.com',
	'deadaddress.com',
	'deadchildren.org',
	'deadfake.cf',
	'deadfake.ga',
	'deadfake.ml',
	'deadfake.tk',
	'deadspam.com',
	'deagot.com',
	'deajeng.store',
	'dealja.com',
	'deallabs.org',
	'dealrek.com',
	'decep.com',
	'decodewp.com',
	'dede.infos.st',
	'deekayen.us',
	'deenur.com',
	'deepconverts.com',
	'deepmails.org',
	'deepmaster.fun',
	'deepyinc.com',
	'defomail.com',
	'degap.fr.nf',
	'degar.xyz',
	'degradedfun.net',
	'deinbox.com',
	'delaeb.com',
	'delayload.com',
	'delayload.net',
	'delikkt.de',
	'delivrmail.com',
	'delorex.com',
	'deluxmail.com',
	'demen.ml',
	'dengekibunko.ga',
	'dengekibunko.gq',
	'dengekibunko.ml',
	'deornaumail.com',
	'der-kombi.de',
	'derkombi.de',
	'derluxuswagen.de',
	'desertsundesigns.com',
	'desfrenes.fr.nf',
	'deshnetarchadacalculator.one',
	'desoz.com',
	'despam.it',
	'despammed.com',
	'dev-null.cf',
	'dev-null.ga',
	'dev-null.gq',
	'dev-null.ml',
	'devbike.com',
	'developermail.com',
	'devnullmail.com',
	'deyom.com',
	'dharmatel.net',
	'dhm.ro',
	'dhnow.com',
	'dhy.cc',
	'dialogus.com',
	'diapaulpainting.com',
	'dichvuxe24h.com',
	'dicopto.com',
	'diemhenvn.com',
	'digdig.org',
	'digi-value.fr',
	'diginey.com',
	'digital-message.com',
	'digitalesbusiness.info',
	'digitalmail.info',
	'digitalmariachis.com',
	'digitalsanctuary.com',
	'dildosfromspace.com',
	'dim-coin.com',
	'dimalk.com',
	'dingbone.com',
	'diolang.com',
	'directmail24.net',
	'dis.hopto.org',
	'disaq.com',
	'disbox.net',
	'disbox.org',
	'discard.cf',
	'discard.email',
	'discard.ga',
	'discard.gq',
	'discard.ml',
	'discard.tk',
	'discardmail.com',
	'discardmail.de',
	'discos4.com',
	'dishcatfish.com',
	'disign-concept.eu',
	'disign-revelation.com',
	'dispo.in',
	'dispomail.eu',
	'disposable-e.ml',
	'disposable-email.ml',
	'disposable.cf',
	'disposable.ga',
	'disposable.ml',
	'disposable.site',
	'disposableaddress.com',
	'disposableemailaddresses.com',
	'disposableinbox.com',
	'disposablemails.com',
	'dispose.it',
	'disposeamail.com',
	'disposely.xyz',
	'disposemail.com',
	'disposemymail.com',
	'disposly.com',
	'dispostable.com',
	'divad.ga',
	'divermail.com',
	'divismail.ru',
	'diwaq.com',
	'dizigg.com',
	'djiv.xyz',
	'dldweb.info',
	'dlemail.ru',
	'dmarc.ro',
	'dmts.fr.nf',
	'dmxs8.com',
	'dndent.com',
	'dnsclick.com',
	'dnses.ro',
	'doanart.com',
	'dob.jp',
	'dodgeit.com',
	'dodgemail.de',
	'dodgit.com',
	'dodgit.org',
	'dodsi.com',
	'dogclothing.org',
	'doiea.com',
	'doj.one',
	'dolofan.com',
	'dolphinnet.net',
	'domforfb1.tk',
	'domforfb18.tk',
	'domforfb19.tk',
	'domforfb2.tk',
	'domforfb23.tk',
	'domforfb27.tk',
	'domforfb29.tk',
	'domforfb3.tk',
	'domforfb4.tk',
	'domforfb5.tk',
	'domforfb6.tk',
	'domforfb7.tk',
	'domforfb8.tk',
	'domforfb9.tk',
	'domozmail.com',
	'don.edu.pl',
	'dona.one',
	'dona.pw',
	'dona.rip',
	'donebyngle.com',
	'donemail.my.id',
	'donemail.ru',
	'dongqing365.com',
	'dontreg.com',
	'dontsendmespam.de',
	'doobb.com',
	'doojazz.com',
	'doquier.tk',
	'dotapodemail.com',
	'dotman.de',
	'dotmsg.com',
	'dotslashrage.com',
	'dotvu.net',
	'dotzi.net',
	'doublemail.de',
	'douchelounge.com',
	'doulas.org',
	'dozvon-spb.ru',
	'dp76.com',
	'dpmurt.my',
	'dpptd.com',
	'dr69.site',
	'drdrb.com',
	'drdrb.net',
	'dreamclarify.org',
	'dreamgreen.fr.nf',
	'dred.ru',
	'drevo.si',
	'drewzen.com',
	'driftz.net',
	'dripzgaming.com',
	'drivetagdev.com',
	'drivz.net',
	'drmail.in',
	'droolingfanboy.de',
	'dropcake.de',
	'dropinboxes.com',
	'dropjar.com',
	'droplar.com',
	'dropmail.me',
	'dropsin.net',
	'drowblock.com',
	'drugsellers.com',
	'druzik.pp.ua',
	'dsfsfdsfds.shop',
	'dsgvo.party',
	'dsgvo.ru',
	'dshfjdafd.cloud',
	'dsiay.com',
	'dsitip.com',
	'dspwebservices.com',
	'duam.net',
	'dubokutv.com',
	'duck2.club',
	'dudmail.com',
	'dugmail.com',
	'duk33.com',
	'dukedish.com',
	'dulich84.com',
	'dumalu.com',
	'dumbass.nl',
	'dump-email.info',
	'dumpandjunk.com',
	'dumpmail.de',
	'dumpyemail.com',
	'dunkos.xyz',
	'durandinterstellar.com',
	'duskmail.com',
	'dusrui.com',
	'dv2.host',
	'dvdpit.com',
	'dwse.edu.pl',
	'dyceroprojects.com',
	'dygovil.com',
	'dz17.net',
	'e-mail.com',
	'e-mail.org',
	'e-marketstore.ru',
	'e-pool.co.uk',
	'e-pool.uk',
	'e-record.com',
	'e-tomarigi.com',
	'e3z.de',
	'e4ward.com',
	'eacademia.uk',
	'ealea.fr.nf',
	'eanok.com',
	'eartin.net',
	'ease.es',
	'easy-trash-mail.com',
	'easyhomefit.com',
	'easymailer.live',
	'easymails.cc',
	'easynetwork.info',
	'easytrashmail.com',
	'eatmea2z.club',
	'eay.jp',
	'ebbob.com',
	'ebeschlussbuch.de',
	'ecallheandi.com',
	'ecolo-online.fr',
	'ecoverseworld.com',
	'ecstor.com',
	'edgepodlab.com',
	'edgex.ru',
	'edinburgh-airporthotels.com',
	'edmondventures.com',
	'edny.net',
	'edudingy.cfd',
	'edumail.edu.pl',
	'edumail.edu.rs',
	'edupolska.edu.pl',
	'edv.to',
	'ee1.pl',
	'ee2.pl',
	'eeedv.de',
	'eelmail.com',
	'eelraodo.com',
	'eewmaop.com',
	'effobe.com',
	'efxs.ca',
	'eggur.com',
	'egirl.help',
	'egvoo.com',
	'egzones.com',
	'einmalmail.de',
	'einrot.com',
	'einrot.de',
	'eintagsmail.de',
	'eiveg.com',
	'eldobhato-level.hu',
	'elearningjournal.org',
	'electro.mn',
	'elerso.com',
	'eligou.store',
	'elitevipatlantamodels.com',
	'elki-mkzn.ru',
	'elobits.com',
	'elygifts.com',
	'email-68.com',
	'email-fake.cf',
	'email-fake.com',
	'email-fake.ga',
	'email-fake.gq',
	'email-fake.ml',
	'email-fake.tk',
	'email-free.online',
	'email-jetable.fr',
	'email-lab.com',
	'email-temp.com',
	'email.edu.pl',
	'email.net',
	'email1.pro',
	'email10p.org',
	'email60.com',
	'email84.com',
	'emailabox.pro',
	'emailage.cf',
	'emailage.ga',
	'emailage.gq',
	'emailage.ml',
	'emailage.tk',
	'emailaoa.pro',
	'emailate.com',
	'emailawb.pro',
	'emailbbox.pro',
	'emailbin.net',
	'emailboxer.one',
	'emailcbox.pro',
	'emailcoffeehouse.com',
	'emailcu.icu',
	'emaildark.fr.nf',
	'emaildbox.pro',
	'emaildienst.de',
	'emaildrop.io',
	'emailfake.com',
	'emailfake.ml',
	'emailforyou.info',
	'emailfoxi.pro',
	'emailfreedom.ml',
	'emailgen.uk',
	'emailgenerator.de',
	'emailgo.de',
	'emailias.com',
	'emailigo.de',
	'emailinfive.com',
	'emailisvalid.com',
	'emailkp.com',
	'emaillime.com',
	'emailmiser.com',
	'emailna.co',
	'emailnator.com',
	'emailnax.com',
	'emailnow.net',
	'emailnow.one',
	'emailo.pro',
	'emailondeck.com',
	'emailportal.info',
	'emailproxsy.com',
	'emailracc.com',
	'emailresort.com',
	'emailrod.com',
	'emails.ga',
	'emailsecurer.com',
	'emailsensei.com',
	'emailsingularity.net',
	'emailspam.cf',
	'emailspam.ga',
	'emailspam.gq',
	'emailspam.ml',
	'emailspam.tk',
	'emailsy.info',
	'emailtech.info',
	'emailtemporanea.com',
	'emailtemporanea.net',
	'emailtemporar.ro',
	'emailtemporario.com.br',
	'emailthe.net',
	'emailtik.com',
	'emailtmp.com',
	'emailto.de',
	'emailure.net',
	'emailvb.pro',
	'emailwarden.com',
	'emailwww.pro',
	'emailxfer.com',
	'emaily.pro',
	'emailz.cf',
	'emailz.ga',
	'emailz.gq',
	'emailz.ml',
	'emaxasp.com',
	'embekhoe.com',
	'emeil.in',
	'emeil.ir',
	'emeraldwebmail.com',
	'emergencymail.site',
	'emergentvillage.org',
	'emkei.cf',
	'emkei.ga',
	'emkei.gq',
	'emkei.ml',
	'emkei.tk',
	'eml.pp.ua',
	'emlhot.com',
	'emlhub.com',
	'emlpro.com',
	'emltmp.com',
	'emocan.name.tr',
	'empireanime.ga',
	'emstjzh.com',
	'emz.net',
	'enayu.com',
	'end.tw',
	'endob.com',
	'enotj.com',
	'enterto.com',
	'envy17.com',
	'eoffice.top',
	'eoopy.com',
	'eosada.com',
	'eoslux.com',
	'eoutrbl.com',
	'ep77.com',
	'epb.ro',
	'epbox.ru',
	'epbox.store',
	'ephemail.net',
	'ephemeral.email',
	'eposta.buzz',
	'eposta.work',
	'epostal.ru',
	'epostal.store',
	'eqiluxspam.ga',
	'ereplyzy.com',
	'ericjohnson.ml',
	'eripo.net',
	'ero-tube.org',
	'erth.nl',
	'esadverse.com',
	'esbano-ru.ru',
	'esc.la',
	'escapehatchapp.com',
	'esemay.com',
	'esgeneri.com',
	'esiix.com',
	'esprity.com',
	'estate-invest.fr',
	'esterace.com',
	'etempmail.net',
	'etenx.com',
	'eth2btc.info',
	'ether123.net',
	'ethereum1.top',
	'ethersports.org',
	'ethersportz.info',
	'etics.us',
	'etotvibor.ru',
	'etramay.com',
	'etranquil.com',
	'etranquil.net',
	'etranquil.org',
	'etubemail.com',
	'euaqa.com',
	'euleina.com',
	'eurokool.com',
	'euucn.com',
	'evanfox.info',
	'eveav.com',
	'evilcomputer.com',
	'evilgodshop.uk',
	'evnft.com',
	'evopo.com',
	'evusd.com',
	'evvgo.com',
	'evyush.com',
	'ewebrus.com',
	'exahut.com',
	'examstudy.xyz',
	'exbts.com',
	'exclussi.com',
	'exdonuts.com',
	'exelica.com',
	'exespay.com',
	'exile.my.id',
	'existiert.net',
	'exitbit.com',
	'exitstageleft.net',
	'exmab.com',
	'expiredtoaster.org',
	'explodemail.com',
	'express.net.ua',
	'extracurricularsociety.com',
	'extraku.net',
	'extraku.shop',
	'extremail.ru',
	'exweme.com',
	'eyepaste.com',
	'eynlong.com',
	'ez.lv',
	'ezehe.com',
	'ezfill.com',
	'ezonemail.com',
	'ezstest.com',
	'ezua.com',
	'ezztt.com',
	'f4k.es',
	'fabaos.com',
	'facebook-email.cf',
	'facebook-email.ga',
	'facebook-email.ml',
	'facebookmail.gq',
	'facebookmail.ml',
	'facemac.website',
	'fackme.gq',
	'fadingemail.com',
	'faecesmail.me',
	'fag.wf',
	'failbone.com',
	'faithkills.com',
	'fake-box.com',
	'fake-email.pp.ua',
	'fake-mail.cf',
	'fake-mail.ga',
	'fake-mail.ml',
	'fakedemail.com',
	'fakeinbox.cf',
	'fakeinbox.com',
	'fakeinbox.ga',
	'fakeinbox.info',
	'fakeinbox.ml',
	'fakeinbox.tk',
	'fakeinformation.com',
	'fakemail.fr',
	'fakemail.io',
	'fakemail.net',
	'fakemailgenerator.com',
	'fakemailz.com',
	'fallinhay.com',
	'famamail.com',
	'fammix.com',
	'fanclub.pm',
	'fandoe.com',
	'fangoh.com',
	'fanicle.com',
	'fansworldwide.de',
	'fantastu.com',
	'fantasymail.de',
	'farah.rip',
	'farrse.co.uk',
	'fasssd.ru',
	'fasssd.store',
	'fast-email.info',
	'fast-mail.fr',
	'fastacura.com',
	'fastchevy.com',
	'fastchrysler.com',
	'fasternet.biz',
	'fastestflex.com',
	'fastkawasaki.com',
	'fastmail.edu.pl',
	'fastmazda.com',
	'fastmitsubishi.com',
	'fastnissan.com',
	'fastsearcher.com',
	'fastsubaru.com',
	'fastsuzuki.com',
	'fasttoyota.com',
	'fastyamaha.com',
	'fatflap.com',
	'faxico.com',
	'faybetsy.com',
	'fbhotro.com',
	'fbi.one',
	'fbma.tk',
	'fddns.ml',
	'fdfdsfds.com',
	'featcore.com',
	'femailtor.com',
	'fer-gabon.org',
	'feralrex.com',
	'ferdomnermail.com',
	'feriwor.com',
	'fermaxxi.ru',
	'feroxid.com',
	'fettometern.com',
	'fexbox.org',
	'fexbox.ru',
	'fexpost.com',
	'fextemp.com',
	'fft.edu.do',
	'fhpfhp.fr.nf',
	'fiallaspares.com',
	'fibmail.com',
	'ficken.de',
	'fictionsite.com',
	'fightallspam.com',
	'figjs.com',
	'figshot.com',
	'figurescoin.com',
	'fiifke.de',
	'filbert4u.com',
	'filberts4u.com',
	'file2drive.com',
	'filetodrive.com',
	'film-blog.biz',
	'filmla.org',
	'filzmail.com',
	'finacenter.com',
	'findemail.info',
	'findu.pl',
	'finews.biz',
	'fingso.com',
	'fir.hk',
	'fira.my',
	'firemailbox.club',
	'firstlawyer.org',
	'fitnesrezink.ru',
	'fivemail.de',
	'fivermail.com',
	'fixmail.tk',
	'fizmail.com',
	'fkainc.com',
	'flaimenet.ir',
	'fleckens.hu',
	'flemail.ru',
	'flexvio.com',
	'fliegender.fish',
	'flobo.fr.nf',
	'flock84.uk',
	'flosek.com',
	'flowu.com',
	'flu.cc',
	'fluidsoft.us',
	'flurred.com',
	'fly-ts.de',
	'flyinggeek.net',
	'flymail.tk',
	'flyovertrees.com',
	'flyspam.com',
	'flywaverun.com',
	'flyzy.net',
	'fncp.ru',
	'fncp.store',
	'fog.one',
	'foobarbot.net',
	'footard.com',
	'foreastate.com',
	'forecastertests.com',
	'foreskin.cf',
	'foreskin.ga',
	'foreskin.gq',
	'foreskin.ml',
	'foreskin.tk',
	'forexnews.bg',
	'forexru.com',
	'forexzig.com',
	'forgetmail.com',
	'formodapk.com',
	'fornow.eu',
	'forspam.net',
	'forward.cat',
	'fosil.pro',
	'foxja.com',
	'foxnew.info',
	'foxtrotter.info',
	'fr.cr',
	'fr33mail.info',
	'fragolina2.tk',
	'frapmail.com',
	'frappina.tk',
	'frederictonlawyer.com',
	'free-email.cf',
	'free-email.ga',
	'free-temp-mail.eu.org',
	'free-temp.net',
	'freebabysittercam.com',
	'freeblackbootytube.com',
	'freecat.net',
	'freecustom.email',
	'freedom4you.info',
	'freedompop.us',
	'freedrops.org',
	'freefattymovies.com',
	'freehotmail.net',
	'freeimagehosts.org',
	'freeimghost.org',
	'freeinbox.email',
	'freelance-france.eu',
	'freeletter.me',
	'freemail.ms',
	'freemails.cf',
	'freemails.ga',
	'freemails.ml',
	'freemails.pp.ua',
	'freemeil.ga',
	'freemeil.gq',
	'freemeil.ml',
	'freeml.net',
	'freeplumpervideos.com',
	'freerubli.ru',
	'freeschoolgirlvids.com',
	'freesistercam.com',
	'freesourcecodes.com',
	'freeteenbums.com',
	'freetimer.online',
	'freundin.ru',
	'friendlymail.co.uk',
	'frm.ovh',
	'front14.org',
	'frostmail.fr.nf',
	'frwdmail.com',
	'fsitip.com',
	'fthcapital.com',
	'ftp.sh',
	'ftpinc.ca',
	'fuckedupload.com',
	'fuckingduh.com',
	'fuckme69.club',
	'fucknloveme.top',
	'fuckxxme.top',
	'fudgerub.com',
	'fudier.com',
	'fuirio.com',
	'fukaru.com',
	'fukurou.ch',
	'fullangle.org',
	'fulvie.com',
	'fumemail.com',
	'fun4k.com',
	'fun64.com',
	'fundapk.com',
	'fundproceed.com',
	'funnycodesnippets.com',
	'funnymail.de',
	'funteka.com',
	'furzauflunge.de',
	'fusioninbox.com',
	'futuramind.com',
	'fuvk.ru',
	'fuvk.store',
	'fuwa.be',
	'fuwa.li',
	'fuwamofu.com',
	'fuwari.be',
	'fux0ringduh.com',
	'fviadropinbox.com',
	'fviainboxes.com',
	'fviamail.com',
	'fviamail.work',
	'fxavaj.com',
	'fxnxs.com',
	'fxtubes.com',
	'fxzig.com',
	'fycloud.online',
	'fyii.de',
	'fynix.sbs',
	'g-mail.kr',
	'g14l71lb.com',
	'g1xmail.top',
	'g2xmail.top',
	'g3xmail.top',
	'g4hdrop.us',
	'gafy.net',
	'gagcalculator.me',
	'gage.ga',
	'galaxim.fr.nf',
	'galaxy.tv',
	'gally.jp',
	'gamail.top',
	'gamebcs.com',
	'gamegregious.com',
	'gamepec.com',
	'gamgling.com',
	'garasikita.pw',
	'garbagecollector.org',
	'garbagemail.org',
	'gardenscape.ca',
	'garizo.com',
	'garliclife.com',
	'garrymccooey.com',
	'gassmail.com',
	'gasuda.com',
	'gav0.com',
	'gavrom.com',
	'gawab.com',
	'gaxetovemail.com',
	'gbcmail.win',
	'gbmail.top',
	'gcmail.top',
	'gddp2018.edu.vn',
	'gdmail.top',
	'gdqoe.net',
	'gedmail.win',
	'geekforex.com',
	'geew.ru',
	'gehensiemirnichtaufdensack.de',
	'geldwaschmaschine.de',
	'gelitik.in',
	'gemapan.com',
	'genderfuck.net',
	'generator.email',
	'genzmaile.com',
	'genzotp.com',
	'geronra.com',
	'geschent.biz',
	'get-mail.cf',
	'get-mail.ga',
	'get-mail.ml',
	'get-mail.tk',
	'get.pp.ua',
	'get1mail.com',
	'get2mail.fr',
	'getairmail.cf',
	'getairmail.com',
	'getairmail.ga',
	'getairmail.gq',
	'getairmail.ml',
	'getairmail.tk',
	'geteit.com',
	'getfun.men',
	'getmail1.com',
	'getmailfree.cc',
	'getmails.eu',
	'getmule.com',
	'getnada.cc',
	'getnada.com',
	'getnowtoday.cf',
	'getol.pro',
	'getonemail.com',
	'getonemail.net',
	'getover.de',
	'getsimpleemail.com',
	'gett.icu',
	'gexik.com',
	'ggmal.ml',
	'ggrainn.com',
	'ggvk.ru',
	'ggvk.store',
	'ggxhunter.com',
	'ghk55.us',
	'gholar.com',
	'ghosttexter.de',
	'ghvv.click',
	'giacmosuaviet.info',
	'giaiphapmuasam.com',
	'giantmail.de',
	'gifto12.com',
	'gimbmail.com',
	'gimme-cooki.es',
	'gimpmail.com',
	'gimuemoa.fr.nf',
	'ginzi.be',
	'ginzi.co.uk',
	'ginzi.es',
	'ginzi.net',
	'ginzy.co.uk',
	'ginzy.eu',
	'giodaingan.com',
	'gioidev.news',
	'giratex.com',
	'girlfriend.ru',
	'girlmail.win',
	'girlsindetention.com',
	'girlsundertheinfluence.com',
	'gishpuppy.com',
	'giveh2o.info',
	'givememail.club',
	'givmail.com',
	'gixenmixen.com',
	'gladogmi.fr.nf',
	'glitch.sx',
	'globalbizflow.com',
	'globaltouron.com',
	'glubex.com',
	'glucosegrin.com',
	'gmail2.gq',
	'gmailot.com',
	'gmatch.org',
	'gmial.com',
	'gmx1mail.top',
	'gmxmail.top',
	'gmxmail.win',
	'gnctr-calgary.com',
	'gng.edu.pl',
	'gni8.com',
	'go2usa.info',
	'go2vpn.net',
	'goatmail.uk',
	'godfare.com',
	'goemailgo.com',
	'goeschman.com',
	'gold.edu.pl',
	'goldenmagpies.com',
	'goldinbox.net',
	'golemico.com',
	'goleudy.org.uk',
	'goliszek.net',
	'gomail.in',
	'gongjua.com',
	'gonida.co.uk',
	'gonida.com',
	'gonida.uk',
	'googl.win',
	'goomail.club',
	'goonby.com',
	'gooner.cat',
	'goplaygame.ru',
	'gorillaswithdirtyarmpits.com',
	'goround.info',
	'gosarlar.com',
	'gosuslugi-spravka.ru',
	'gothere.biz',
	'gotmail.com',
	'gotmail.net',
	'gotmail.org',
	'gowikibooks.com',
	'gowikicampus.com',
	'gowikicars.com',
	'gowikifilms.com',
	'gowikigames.com',
	'gowikimusic.com',
	'gowikinetwork.com',
	'gowikitravel.com',
	'gowikitv.com',
	'grandmamail.com',
	'grandmasmail.com',
	'grassdev.com',
	'gravityengine.cc',
	'great-host.in',
	'greencafe24.com',
	'greendike.com',
	'greenhousemail.com',
	'greensloth.com',
	'greggamel.com',
	'greggamel.net',
	'gregorsky.zone',
	'gregorygamel.com',
	'gregorygamel.net',
	'grish.de',
	'griuc.schule',
	'grn.cc',
	'groklan.com',
	'groupbuff.com',
	'grr.la',
	'gruene-no-thanks.xyz',
	'grugrug.ru',
	'gruz-m.ru',
	'gs-arc.org',
	'gsredcross.org',
	'gsrv.co.uk',
	'gsxstring.ga',
	'guanshuyun.com',
	'gudanglowongan.com',
	'guerillamail.biz',
	'guerillamail.com',
	'guerillamail.de',
	'guerillamail.info',
	'guerillamail.net',
	'guerillamail.org',
	'guerillamailblock.com',
	'guerrillamail.biz',
	'guerrillamail.com',
	'guerrillamail.de',
	'guerrillamail.info',
	'guerrillamail.net',
	'guerrillamail.org',
	'guerrillamailblock.com',
	'gufum.com',
	'gukox.org',
	'gustr.com',
	'guysmail.com',
	'gxemail.men',
	'gyan-netra.com',
	'gyknife.com',
	'gynzi.co.uk',
	'gynzi.es',
	'gzb.ro',
	'h0tmaii.com',
	'h2beta.com',
	'h8s.org',
	'ha92.store',
	'haben-wir.com',
	'habenwir.com',
	'habitue.net',
	'hacccc.com',
	'hackersquad.tk',
	'hackthatbit.ch',
	'hahawrong.com',
	'haibabon.com',
	'haida-edu.cn',
	'hairs24.ru',
	'haltospam.com',
	'hamham.uk',
	'handrik.com',
	'hangxomcuatoilatotoro.ml',
	'happiseektest.com',
	'happy-new-year.top',
	'happy2023year.com',
	'happy9toy.com',
	'happydomik.ru',
	'harakirimail.com',
	'haren.uk',
	'haribu.com',
	'hartbot.de',
	'hasanmail.ml',
	'hat-geld.de',
	'hatespam.org',
	'hawrong.com',
	'haydoo.com',
	'hazelnut4u.com',
	'hazelnuts4u.com',
	'hazmatshipping.org',
	'hccmail.win',
	'headstrong.de',
	'healthforwomen.info',
	'healxo.org',
	'heathenhammer.com',
	'heathenhero.com',
	'hecat.es',
	'heheee.com',
	'heisei.be',
	'hellodream.mobi',
	'helloricky.com',
	'helpinghandtaxcenter.org',
	'helpjobs.ru',
	'heros3.com',
	'herp.in',
	'herpderp.nl',
	'hetzez.com',
	'hezll.com',
	'hh7f.com',
	'hhe.org.uk',
	'hi2.in',
	'hi5.si',
	'hiddentragedy.com',
	'hidebox.org',
	'hidebusiness.xyz',
	'hidemail.de',
	'hidemail.pro',
	'hidemail.us',
	'hidesmail.net',
	'hidmail.org',
	'hidzz.com',
	'hiemail.net',
	'hieuclone.net',
	'highbros.org',
	'highstar.shop',
	'hihi.lol',
	'hikuhu.com',
	'hiltonvr.com',
	'him6.com',
	'himail.infos.st',
	'himail.online',
	'hitbase.net',
	'hldrive.com',
	'hlkes.com',
	'hmail.us',
	'hmamail.com',
	'hmh.ro',
	'hoanggiaanh.com',
	'hoanglong.tech',
	'hochsitze.com',
	'hola.org',
	'holio.day',
	'holl.ga',
	'homecut.pro',
	'honesthirianinda.net',
	'honeys.be',
	'honor-8.com',
	'hook2ad.com',
	'hooooooo.store',
	'hopemail.biz',
	'horizonspost.com',
	'hornyalwary.top',
	'host1s.com',
	'hostcalls.com',
	'hostguru.top',
	'hostingmail.me',
	'hostlaba.com',
	'hot-mail.cf',
	'hot-mail.ga',
	'hot-mail.gq',
	'hot-mail.ml',
	'hot-mail.tk',
	'hotlinkimg.com',
	'hotmai.com',
	'hotmailproduct.com',
	'hotmial.com',
	'hotpop.com',
	'hotprice.co',
	'hotrokh.com',
	'hotsoup.be',
	'hottchurch.org.uk',
	'housat.com',
	'housereformas.es',
	'hpari.com',
	'hpc.tw',
	'hs.vc',
	'hsmw.net',
	'ht.cx',
	'hthlm.com',
	'httpsgreenwichmeantime.in',
	'huangniu8.com',
	'hubglee.com',
	'hubhost.store',
	'hudisk.com',
	'huizk.com',
	'hukkmu.tk',
	'hulapla.de',
	'humaility.com',
	'hungpackage.com',
	'hunnur.com',
	'huongdanfb.com',
	'hushmail.cf',
	'huskion.net',
	'hutudns.com',
	'hvastudiesucces.nl',
	'hwsye.net',
	'hxopi.ru',
	'hxopi.store',
	'hypenated-domain.com',
	'i-dork.com',
	'i2pmail.org',
	'i6.cloudns.cc',
	'iaoss.com',
	'ibande.xyz',
	'ibnuh.bz',
	'ibolinva.com',
	'ibymail.com',
	'icantbelieveineedtoexplainthisshit.com',
	'icbr.us',
	'icemail.club',
	'ich-essen-fleisch.bio',
	'ichigo.me',
	'icidroit.info',
	'icmans.com',
	'iconmal.com',
	'icousd.com',
	'icx.in',
	'icx.ro',
	'icznn.com',
	'ideuse.com',
	'idf.ovh',
	'idfd.live',
	'idont.date',
	'idx4.com',
	'idxue.com',
	'ieatspam.eu',
	'ieatspam.info',
	'ieh-mail.de',
	'iencm.com',
	'iffymedia.com',
	'ifoxdd.com',
	'ige.es',
	'igg.biz',
	'ignoremail.com',
	'ihateyoualot.info',
	'ihazspam.ca',
	'iheartspam.org',
	'ihnpo.food',
	'ikbenspamvrij.nl',
	'ikewe.com',
	'ikomail.com',
	'ikuromi.com',
	'illistnoise.com',
	'illubd.com',
	'ilovespam.com',
	'im5z.com',
	'imail.edu.vn',
	'imail1.net',
	'imails.asso.st',
	'imails.info',
	'imailt.com',
	'imap.fr.nf',
	'imfaya.com',
	'img-free.com',
	'imgof.com',
	'imgv.de',
	'immo-gerance.info',
	'imnart.com',
	'imperialcnk.com',
	'importemail.com',
	'imstations.com',
	'imul.info',
	'in-ulm.de',
	'in2reach.com',
	'inactivemachine.com',
	'inbax.tk',
	'inbound.plus',
	'inbox.si',
	'inbox2.info',
	'inboxalias.com',
	'inboxbear.com',
	'inboxclean.com',
	'inboxclean.org',
	'inboxdesign.me',
	'inboxed.im',
	'inboxed.pw',
	'inboxkitten.com',
	'inboxnow.ru',
	'inboxnow.store',
	'inboxorigin.com',
	'inboxproxy.com',
	'inboxstore.me',
	'inclick.net',
	'inclusiveprogress.com',
	'incognitomail.com',
	'incognitomail.net',
	'incognitomail.org',
	'incq.com',
	'inctart.com',
	'ind.st',
	'indevgo.com',
	'index-mail.com',
	'indianahorsecouncil.org',
	'indieclad.com',
	'indigobook.com',
	'indirect.ws',
	'indomaed.pw',
	'indomina.cf',
	'indoserver.stream',
	'indosukses.press',
	'ineec.net',
	'infobuzzsite.com',
	'infocom.zp.ua',
	'inggo.org',
	'inilogic.com',
	'inkiny.com',
	'inkomail.com',
	'inlook.cloud',
	'inmynetwork.tk',
	'inoutmail.de',
	'inoutmail.eu',
	'inoutmail.info',
	'inoutmail.net',
	'inpwa.com',
	'insanony.art',
	'insanony.one',
	'insanony.store',
	'insanumingeniumhomebrew.com',
	'insgrmail.site',
	'inshuan.com',
	'insorg-mail.info',
	'instaddr.ch',
	'instaddr.uk',
	'instaddr.win',
	'instance-email.com',
	'instant-mail.de',
	'instantblingmail.info',
	'instantemailaddress.com',
	'instantletter.net',
	'instantmail.fr',
	'instmail.uk',
	'instrete.com',
	'intady.com',
	'internet-v-stavropole.ru',
	'internetkeno.com',
	'internetoftags.com',
	'interstats.org',
	'intersteller.com',
	'intobx.com',
	'intopwa.com',
	'intopwa.net',
	'intopwa.org',
	'introex.com',
	'investore.co',
	'inwagit.com',
	'ionq.pl',
	'iotf.net',
	'iozak.com',
	'ip4.pp.ua',
	'ip6.li',
	'ip6.pp.ua',
	'ipaddressforme.com',
	'ipoo.org',
	'ippandansei.tk',
	'ipsur.org',
	'ipxwan.com',
	'irabops.com',
	'iralborz.bid',
	'iraniandsa.org',
	'irc.so',
	'iridales.com',
	'irish2me.com',
	'irishspringrealty.com',
	'iroid.com',
	'ironiebehindert.de',
	'irpine.com',
	'irssi.tv',
	'is.af',
	'isdaq.com',
	'isep.fr.nf',
	'isfew.com',
	'ishop2k.com',
	'ismartsense.online',
	'isosq.com',
	'ispeedtest.digital',
	'ist-hier.com',
	'istii.ro',
	'isukrainestillacountry.com',
	'it7.ovh',
	'italy-mail.com',
	'itaolo.com',
	'itcompu.com',
	'itfast.net',
	'itsbds.com',
	'itsedit.click',
	'itsjiff.com',
	'itspid.com',
	'itunesgiftcodegenerator.com',
	'iuanhoi.store',
	'iubridge.com',
	'iuemail.men',
	'iwatermail.com',
	'iwi.net',
	'ixaks.com',
	'ixhale.com',
	'ixunbo.com',
	'ixx.io',
	'iya.fr.nf',
	'iysqv.com',
	'j-p.us',
	'jabmag.com',
	'jafps.com',
	'jaga.email',
	'jagomail.com',
	'jailbreakeverything.com',
	'jaipas.lu',
	'jajxz.com',
	'jakemsr.com',
	'janproz.com',
	'japnc.com',
	'jaqis.com',
	'jasonbella.online',
	'javadmin.com',
	'javaemail.com',
	'jcnorris.com',
	'jdmadventures.com',
	'jdz.ro',
	'je-recycle.info',
	'jellow.ml',
	'jellyrolls.com',
	'jeoce.com',
	'jet-renovation.fr',
	'jetable.com',
	'jetable.fr.nf',
	'jetable.net',
	'jetable.org',
	'jetable.pp.ua',
	'ji5.de',
	'ji6.de',
	'ji7.de',
	'jibbo01.com',
	'jincer.com',
	'jinva.fr.nf',
	'jiooq.com',
	'jioso.com',
	'jjj.ee',
	'jjk.app',
	'jkotypc.com',
	'jmail.fr.nf',
	'jmail.ovh',
	'jmail.ro',
	'jnxjn.com',
	'jobbikszimpatizans.hu',
	'jobbrett.com',
	'jobposts.net',
	'jobs-to-be-done.net',
	'jobzyy.com',
	'joelpet.com',
	'joetestalot.com',
	'jofuso.com',
	'jopho.com',
	'joseihorumon.info',
	'josse.ltd',
	'jourrapide.com',
	'jp-ml.com',
	'jpco.org',
	'jsncos.com',
	'jsrsolutions.com',
	'jualfb.co',
	'jumonji.tk',
	'jungkamushukum.com',
	'junk.to',
	'junk1e.com',
	'junkmail.ga',
	'junkmail.gq',
	'just-email.com',
	'justdefinition.com',
	'justemail.ml',
	'juyouxi.com',
	'jwork.ru',
	'jxpomup.com',
	'k-global.dev',
	'k4money.com',
	'kabamail.com',
	'kabinbilla.com',
	'kademen.com',
	'kadokawa.cf',
	'kadokawa.ga',
	'kadokawa.gq',
	'kadokawa.ml',
	'kadokawa.tk',
	'kaengu.ru',
	'kagi.be',
	'kakadua.net',
	'kakao-mail.com',
	'kakslsie.store',
	'kalapi.org',
	'kamen-market.ru',
	'kamete.org',
	'kamsg.com',
	'kanonmail.com',
	'kantclass.com',
	'kaoing.com',
	'kaovo.com',
	'kappala.info',
	'kapten.site',
	'kara-turk.net',
	'karatraman.ml',
	'karenkey.com',
	'kariplan.com',
	'karta-kykyruza.ru',
	'kartvelo.com',
	'kasmail.com',
	'kaspop.com',
	'katztube.com',
	'kazelink.ml',
	'kbox.li',
	'kcoporation.com',
	'kcrw.de',
	'kd2.org',
	'keecs.com',
	'keepmymail.com',
	'kein.date',
	'keinhirn.de',
	'keipino.de',
	'kekita.com',
	'kelenson.com',
	'kellychibale-researchgroup-uct.com',
	'kemail.uk',
	'kemptvillebaseball.com',
	'kenfern.com',
	'kenhbanme.com',
	'kenhphim.net',
	'kerotu.com',
	'ket-qua.org',
	'ketua.id',
	'khoke.nl',
	'kiani.com',
	'kidaroa.com',
	'killmail.com',
	'killmail.net',
	'killyourtime.com',
	'kimdyn.com',
	'kimsdisk.com',
	'kinda.email',
	'kindamail.com',
	'kingsq.ga',
	'kino-100.ru',
	'kiois.com',
	'kismail.ru',
	'kisoq.com',
	'kissgy.com',
	'kisstwink.com',
	'kitnastar.com',
	'kjkszpjcompany.com',
	'kkmail.be',
	'kkoup.com',
	'kksm.be',
	'klassmaster.com',
	'klassmaster.net',
	'klick-tipp.us',
	'kliknesia.io',
	'klipschx12.com',
	'kloap.com',
	'klovenode.com',
	'kludgemush.com',
	'klzlk.com',
	'kmail.li',
	'kmail.live',
	'kmhow.com',
	'knickerbockerban.de',
	'knilok.com',
	'knmcadibav.com',
	'knol-power.nl',
	'knowledgemd.com',
	'kobrandly.com',
	'kodeholik.site',
	'kodpan.com',
	'kokalo.store',
	'koletter.com',
	'kommunity.biz',
	'kon42.com',
	'konican.com',
	'kontoko.org',
	'konultant-jurist.ru',
	'kook.ml',
	'kopagas.com',
	'kopaka.net',
	'korona-nedvizhimosti.ru',
	'koshu.ru',
	'kosmetik-obatkuat.com',
	'kostenlosemailadresse.de',
	'koszmail.pl',
	'kpay.be',
	'kpooa.com',
	'kpost.be',
	'krd.ag',
	'krsw.tk',
	'kruay.com',
	'krypton.tk',
	'ksmtrck.tk',
	'kuhrap.com',
	'kuku.lu',
	'kulmeo.com',
	'kulturbetrieb.info',
	'kumli.racing',
	'kurzepost.de',
	'kutakbisajauhjauh.gq',
	'kvhrr.com',
	'kvhrs.com',
	'kvhrw.com',
	'kwifa.com',
	'kwift.net',
	'kwilco.net',
	'kwtest.io',
	'kyal.pl',
	'kyois.com',
	'kyriog.fr',
	'kyuusei.fr.nf',
	'kzccv.com',
	'l-c-a.us',
	'l33r.eu',
	'l6factors.com',
	'laafd.com',
	'labetteraverouge.at',
	'labworld.org',
	'lacedmail.com',
	'lackmail.net',
	'lackmail.ru',
	'lacraffe.fr.nf',
	'lacto.info',
	'lags.us',
	'lain.ch',
	'lak.pp.ua',
	'lakelivingstonrealestate.com',
	'lakqs.com',
	'lamasticots.com',
	'lambsauce.de',
	'landmail.co',
	'laoeq.com',
	'laoia.com',
	'lapakqu.com',
	'laptoplonghai.com',
	'larisia.com',
	'larland.com',
	'lashyd.com',
	'last-chance.pro',
	'laste.ml',
	'lastmail.co',
	'lastmail.com',
	'lawlita.com',
	'laxex.ru',
	'laxex.store',
	'laxiw.org',
	'laymro.com',
	'lazyinbox.com',
	'lazyinbox.us',
	'ldaho.biz',
	'ldop.com',
	'ldtp.com',
	'le-tim.ru',
	'leadwizzer.com',
	'lee.mx',
	'leechchannel.com',
	'leeching.net',
	'leetmail.co',
	'legalrc.loan',
	'lellno.gq',
	'lenovog4.com',
	'lerany.com',
	'lerbhe.com',
	'lerch.ovh',
	'lero3.com',
	'lesobprovermail.com',
	'letterhaven.net',
	'letterprotect.net',
	'lettersboxmail.com',
	'letthemeatspam.com',
	'lez.se',
	'lgxscreen.com',
	'lhsdv.com',
	'liamcyrus.com',
	'libinit.com',
	'lifebyfood.com',
	'lifetimefriends.info',
	'lifetotech.com',
	'ligsb.com',
	'likeageek.fr.nf',
	'likemovie.net',
	'likevip.net',
	'lillemap.net',
	'lilo.me',
	'lilspam.com',
	'lilsuite.id',
	'lindenbaumjapan.com',
	'link2mail.net',
	'linkedintuts2016.pw',
	'linkmail.info',
	'linlshe.com',
	'linshiyou.com',
	'linshiyouxiang.net',
	'linuxmail.so',
	'liocbrco.com',
	'lista.cc',
	'litedrop.com',
	'liveradio.tk',
	'livinitlarge.net',
	'lizery.com',
	'lkgn.se',
	'llogin.ru',
	'lnovic.com',
	'loadby.us',
	'loan101.pro',
	'loaoa.com',
	'loapq.com',
	'locanto1.club',
	'locantofuck.top',
	'locantowsite.club',
	'locawin.com',
	'locomodev.net',
	'logicfieldzen.com',
	'logicstreak.com',
	'login-email.cf',
	'login-email.ga',
	'login-email.ml',
	'login-email.tk',
	'logsmarter.net',
	'logular.com',
	'loh.pp.ua',
	'loin.in',
	'lolfreak.net',
	'lolmail.biz',
	'longsbkt.xyz',
	'lookugly.com',
	'lordofmysteries.org',
	'lordsofts.com',
	'lortemail.dk',
	'losemymail.com',
	'love-your.mom',
	'lovecalculatorname.org',
	'lovemeet.faith',
	'lovemeleaveme.com',
	'lpfmgmtltd.com',
	'lr7.us',
	'lr78.com',
	'lroid.com',
	'lru.me',
	'ls-server.ru',
	'lsaar.com',
	'lsereborn.com',
	'lsyx24.com',
	'luckymail.org',
	'lukecarriere.com',
	'lukemail.info',
	'lukop.dk',
	'lumeika.com',
	'lumg.uk',
	'lunor.cfd',
	'lushosa.com',
	'lutech.uk',
	'luv2.us',
	'lxbeta.com',
	'lydir.com',
	'lyfestylecreditsolutions.com',
	'lyft.live',
	'lynex.sbs',
	'lynwise.shop',
	'lyricspad.net',
	'lzoaq.com',
	'm21.cc',
	'm4ilweb.info',
	'ma1l.duckdns.org',
	'mabal.fr.nf',
	'maboard.com',
	'mabox.eu',
	'mac-24.com',
	'machen-wir.com',
	'macmail.info',
	'macr2.com',
	'macromaid.com',
	'macromice.info',
	'magamail.com',
	'magbit.food',
	'maggotymeat.ga',
	'magicbox.ro',
	'magim.be',
	'magspam.net',
	'maidlow.info',
	'mail-card.net',
	'mail-easy.fr',
	'mail-filter.com',
	'mail-help.net',
	'mail-hosting.co',
	'mail-hub.info',
	'mail-mario.fr.nf',
	'mail-now.top',
	'mail-owl.com',
	'mail-share.com',
	'mail-temporaire.com',
	'mail-temporaire.fr',
	'mail-tester.com',
	'mail.by',
	'mail.gw',
	'mail.tm',
	'mail.wtf',
	'mail0.ga',
	'mail1.top',
	'mail101.xyz',
	'mail10m.com',
	'mail114.net',
	'mail12h.com',
	'mail1a.de',
	'mail1web.org',
	'mail21.cc',
	'mail22.club',
	'mail2rss.org',
	'mail333.com',
	'mail4.uk',
	'mail4qa.com',
	'mail4trash.com',
	'mail666.ru',
	'mail7.io',
	'mail707.com',
	'mail72.com',
	'mailadresi.tk',
	'mailapp.top',
	'mailapril.org',
	'mailba.uk',
	'mailback.com',
	'mailbai.com',
	'mailbali.com',
	'mailbidon.com',
	'mailbiscuit.com',
	'mailbiz.biz',
	'mailblocks.com',
	'mailbox.in.ua',
	'mailbox.zip',
	'mailbox49.com',
	'mailbox52.ga',
	'mailbox80.biz',
	'mailbox82.biz',
	'mailbox87.de',
	'mailbox92.biz',
	'mailboxify.ru',
	'mailboxify.store',
	'mailboxly.ru',
	'mailboxly.store',
	'mailboxy.fun',
	'mailboxy.ru',
	'mailboxy.store',
	'mailbucket.org',
	'mailcat.biz',
	'mailcatch.com',
	'mailchop.com',
	'mailcker.com',
	'maildax.me',
	'mailde.de',
	'mailde.info',
	'maildrop.cc',
	'maildrop.cf',
	'maildrop.ga',
	'maildrop.gq',
	'maildrop.ml',
	'maildu.de',
	'maildx.com',
	'maileater.com',
	'mailed.in',
	'mailed.ro',
	'maileimer.de',
	'maileme101.com',
	'mailers.edu.pl',
	'mailexpire.com',
	'mailf5.com',
	'mailfa.tk',
	'mailfall.com',
	'mailfast.pro',
	'mailfirst.icu',
	'mailforspam.com',
	'mailfree.ga',
	'mailfree.gq',
	'mailfree.ml',
	'mailfreeonline.com',
	'mailfs.com',
	'mailguard.me',
	'mailgutter.com',
	'mailhazard.com',
	'mailhazard.us',
	'mailhex.com',
	'mailhub.pro',
	'mailhz.me',
	'mailimate.com',
	'mailin8r.com',
	'mailinatar.com',
	'mailinater.com',
	'mailinator.co.uk',
	'mailinator.com',
	'mailinator.gq',
	'mailinator.info',
	'mailinator.net',
	'mailinator.org',
	'mailinator.us',
	'mailinator0.com',
	'mailinator1.com',
	'mailinator2.com',
	'mailinator2.net',
	'mailinator3.com',
	'mailinator4.com',
	'mailinator5.com',
	'mailinator6.com',
	'mailinator7.com',
	'mailinator8.com',
	'mailinator9.com',
	'mailincubator.com',
	'mailisia.com',
	'mailismagic.com',
	'mailita.tk',
	'mailjunk.cf',
	'mailjunk.ga',
	'mailjunk.gq',
	'mailjunk.ml',
	'mailjunk.tk',
	'maillog.uk',
	'mailmagnet.co',
	'mailmate.com',
	'mailmaxy.one',
	'mailmay.org',
	'mailme.gq',
	'mailme.ir',
	'mailme.lv',
	'mailme24.com',
	'mailmenot.io',
	'mailmetrash.com',
	'mailmoat.com',
	'mailmoth.com',
	'mailms.com',
	'mailna.biz',
	'mailna.co',
	'mailna.in',
	'mailna.me',
	'mailnada.cc',
	'mailnada.com',
	'mailnator.com',
	'mailnesia.com',
	'mailnoop.store',
	'mailnull.com',
	'mailnuo.com',
	'mailonaut.com',
	'mailorc.com',
	'mailorg.org',
	'mailosaur.net',
	'mailox.fun',
	'mailp.org',
	'mailpick.biz',
	'mailpluss.com',
	'mailpooch.com',
	'mailpoof.com',
	'mailpress.gq',
	'mailpro.lat',
	'mailpro.live',
	'mailproxsy.com',
	'mailpull.com',
	'mailquack.com',
	'mailrock.biz',
	'mailsac.com',
	'mailsafe.fr.nf',
	'mailsbay.com',
	'mailscrap.com',
	'mailseal.de',
	'mailshan.com',
	'mailshell.com',
	'mailshiv.com',
	'mailshou.com',
	'mailsiphon.com',
	'mailslapping.com',
	'mailslite.com',
	'mailslurp.com',
	'mailsucker.net',
	'mailt.net',
	'mailt.top',
	'mailtechx.com',
	'mailtemp.info',
	'mailtemple.xyz',
	'mailtemporaire.com',
	'mailtemporaire.fr',
	'mailto.plus',
	'mailtome.de',
	'mailtothis.com',
	'mailtraps.com',
	'mailtrash.net',
	'mailtrix.net',
	'mailtub.com',
	'mailtv.net',
	'mailtv.tv',
	'mailuniverse.co.uk',
	'mailw.site',
	'mailzi.ru',
	'mailzilla.com',
	'mailzilla.org',
	'mailzy.org',
	'mainerfolg.info',
	'mainkask.site',
	'makemenaughty.club',
	'makemetheking.com',
	'malahov.de',
	'malayalamdtp.com',
	'malioter.pro',
	'mama3.org',
	'mamulenok.ru',
	'mandraghen.cf',
	'manifestgenerator.com',
	'mannawo.com',
	'mansiondev.com',
	'maohe.cloud',
	'mark-compressoren.ru',
	'marketlink.info',
	'markmurfin.com',
	'mask03.ru',
	'maskmy.id',
	'masonline.info',
	'maswae.world',
	'matamuasu.ga',
	'matchpol.net',
	'matmayer.com',
	'matra.site',
	'max-mail.org',
	'maxresistance.com',
	'maxturns.com',
	'mbox.re',
	'mbx.cc',
	'mc-fly.be',
	'mcache.net',
	'mcdomaine.fr.nf',
	'mciek.com',
	'mcpservers.one',
	'mdhc.tk',
	'mdz.email',
	'meantinc.com',
	'mebelnu.info',
	'mechanicalresumes.com',
	'mechanicspedia.com',
	'medevsa.com',
	'mediaeast.uk',
	'mediaholy.com',
	'medkabinet-uzi.ru',
	'meepsheep.eu',
	'megasend.org',
	'mehr-bitcoin.de',
	'meidecn.com',
	'meinspamschutz.de',
	'meltedbrownies.com',
	'meltmail.com',
	'meltp.com',
	'memeazon.com',
	'memsg.site',
	'mentonit.net',
	'meocon.org',
	'meomo.store',
	'mepost.pw',
	'merepost.com',
	'merry.pink',
	'meruado.uk',
	'merumart.com',
	'mesemails.fr.nf',
	'mess-mails.fr.nf',
	'messagebeamer.de',
	'messwiththebestdielikethe.rest',
	'metadownload.org',
	'metaintern.net',
	'metalunits.com',
	'mexvat.com',
	'mezimages.net',
	'mfsa.info',
	'mfsa.ru',
	'mfunza.com',
	'mhzayt.online',
	'miaferrari.com',
	'miauj.com',
	'mickaben.fr.nf',
	'midcoastcustoms.com',
	'midcoastcustoms.net',
	'midcoastsolutions.com',
	'midcoastsolutions.net',
	'midiharmonica.com',
	'midlertidig.com',
	'midlertidig.net',
	'midlertidig.org',
	'mierdamail.com',
	'migmail.net',
	'migmail.pl',
	'migumail.com',
	'mihep.com',
	'miistermail.fr',
	'mijnhva.nl',
	'mikfarm.com',
	'mikrotikvietnam.com',
	'mikrotikvn.com',
	'mikrotikx.com',
	'miloras.fr.nf',
	'minefieldmail.com',
	'minhquang2000.com',
	'minimail.gq',
	'ministry-of-silly-walks.de',
	'minsmail.com',
	'mintemail.com',
	'minuteafter.com',
	'minuteinbox.com',
	'mirai.re',
	'misterpinball.de',
	'mitico.org',
	'miucce.com',
	'miwacle.com',
	'mixmail.site',
	'mixzu.net',
	'mji.ro',
	'mjj.edu.ge',
	'mjukglass.nu',
	'mkeya.com',
	'mkpfilm.com',
	'mkzaso.com',
	'ml8.ca',
	'mlanm.online',
	'mlgmail.top',
	'mliok.com',
	'mm.my',
	'mm5.se',
	'mnode.me',
	'moakt.cc',
	'moakt.co',
	'moakt.com',
	'moakt.ws',
	'mobileninja.co.uk',
	'mobilevpn.top',
	'moburl.com',
	'mockmyid.com',
	'modirosa.com',
	'moeri.org',
	'mofu.be',
	'mohmal.com',
	'mohmal.im',
	'mohmal.in',
	'mohmal.tech',
	'moimoi.re',
	'mokook.com',
	'molms.com',
	'momentics.ru',
	'momoi.uk',
	'mona.edu.kg',
	'mona.edu.pl',
	'mona.edu.rs',
	'monachat.tk',
	'monadi.ml',
	'monchu.fr',
	'moncourrier.fr.nf',
	'mondial.asso.st',
	'monemail.fr.nf',
	'moneypipe.net',
	'mongrec.top',
	'monmail.fr.nf',
	'monumentmail.com',
	'moolee.net',
	'moonapps.org',
	'moondyal.com',
	'moonfee.com',
	'moonwake.com',
	'moot.es',
	'moreawesomethanyou.com',
	'moreorcs.com',
	'morriesworld.ml',
	'morsin.com',
	'moruzza.com',
	'motique.de',
	'mountainregionallibrary.net',
	'mowline.com',
	'mox.pp.ua',
	'moy-elektrik.ru',
	'moza.pl',
	'mozej.com',
	'mp-j.ga',
	'mp3oxi.com',
	'mpk.ovh',
	'mr-email.fr.nf',
	'mr24.co',
	'mriscan.live',
	'mriscanner.live',
	'mrotzis.com',
	'mrphoto.org',
	'mrvpm.net',
	'mrvpt.com',
	'msarra.com',
	'msgos.com',
	'mspeciosa.com',
	'msrc.ml',
	'msssg.com',
	'mswork.ru',
	'msxd.com',
	'mt2009.com',
	'mt2014.com',
	'mt2015.com',
	'mtmdev.com',
	'muadaingan.com',
	'muathegame.com',
	'muchomail.com',
	'mucincanon.com',
	'muehlacker.tk',
	'muell.icu',
	'muell.io',
	'muell.monster',
	'muell.xyz',
	'muellemail.com',
	'muellmail.com',
	'muetop.store',
	'mugadget.com',
	'muncloud.com',
	'munik.edu.pl',
	'munoubengoshi.gq',
	'muonwhila.com',
	'musiccode.me',
	'mutant.me',
	'mutudev.com',
	'mvpmedix.com',
	'mvrht.com',
	'mvrht.net',
	'mwarner.org',
	'mxclip.com',
	'mxfuel.com',
	'mxvia.com',
	'my-pomsies.ru',
	'my-teddyy.ru',
	'my10minutemail.com',
	'mybitti.de',
	'mycleaninbox.net',
	'mycorneroftheinter.net',
	'mycreativeinbox.com',
	'myde.ml',
	'mydefipet.live',
	'mydemo.equipment',
	'myecho.es',
	'myemailboxy.com',
	'myfxspot.com',
	'mygeoweb.info',
	'myhochzeitsfilm.de',
	'myindohome.services',
	'myinfoinc.com',
	'myinterserver.ml',
	'mykickassideas.com',
	'myletters.online',
	'mymail-in.net',
	'mymail.infos.st',
	'mymail90.com',
	'mymaildo.kro.kr',
	'mymailoasis.com',
	'mymaily.lol',
	'mynes.com',
	'mynetstore.de',
	'mynoop.store',
	'myopang.com',
	'mypacks.net',
	'mypartyclip.de',
	'myphantomemail.com',
	'mysamp.de',
	'myself.fr.nf',
	'myspaceinc.com',
	'myspaceinc.net',
	'myspaceinc.org',
	'myspacepimpedup.com',
	'myspamless.com',
	'mystvpn.com',
	'mysugartime.ru',
	'mytemp.email',
	'mytempemail.com',
	'mytempmail.com',
	'mytrashmail.com',
	'mywarnernet.net',
	'myweblaw.com',
	'mywrld.site',
	'mywrld.top',
	'myzx.com',
	'mzico.com',
	'n1nja.org',
	'n8.gs',
	'na-cat.com',
	'naah.ru',
	'naah.store',
	'nabaxox.edu.pl',
	'nabuma.com',
	'nada.email',
	'nada.ltd',
	'nagi.be',
	'naka.edu.pl',
	'nakedtruth.biz',
	'namenan.me',
	'namewok.com',
	'nanonym.ch',
	'nanopools.info',
	'naobk.com',
	'naslazhdai.ru',
	'nationalgardeningclub.com',
	'nautonk.com',
	'navalcadets.com',
	'naverapp.com',
	'naverly.com',
	'nawmin.info',
	'naxx.dev',
	'naylonksosmed.com',
	'naymedia.com',
	'nbmbb.com',
	'nbzmr.com',
	'ncien.com',
	'necub.com',
	'negated.com',
	'neghtlefi.com',
	'neko2.net',
	'nekochan.fr',
	'nekosan.uk',
	'neomailbox.com',
	'neotlozhniy-zaim.ru',
	'nepwk.com',
	'nervmich.net',
	'nervtmich.net',
	'nesopf.com',
	'nespf.com',
	'nespj.com',
	'nespressopixie.com',
	'net1mail.com',
	'netcom.ws',
	'netfxd.com',
	'netmails.com',
	'netmails.net',
	'netn.top',
	'netoiu.com',
	'netricity.nl',
	'netris.net',
	'netviewer-france.com',
	'netzidiot.de',
	'nevermail.de',
	'newaddr.com',
	'newbpotato.tk',
	'newbreedapps.com',
	'newfilm24.ru',
	'newideasfornewpeople.info',
	'newmail.top',
	'next.ovh',
	'nextmail.info',
	'nextstopvalhalla.com',
	'nextsuns.com',
	'nezdiro.org',
	'nezid.com',
	'nezumi.be',
	'nezzart.com',
	'nfast.net',
	'nghienplus.io.vn',
	'nguyenusedcars.com',
	'nh3.ro',
	'nhmi1.com',
	'nhmvn.com',
	'nhoopmail.store',
	'nice-4u.com',
	'nicemail.cc',
	'nick-ao.com',
	'nickmxh.com',
	'nicknassar.com',
	'nightorb.com',
	'nikora.fr.nf',
	'nincsmail.com',
	'nincsmail.hu',
	'ninepacman.com',
	'niprack.com',
	'niseko.be',
	'niwl.net',
	'nm123.com',
	'nm7.cc',
	'nmail.cf',
	'nnh.com',
	'nnot.net',
	'nnoway.ru',
	'no-spam.ws',
	'no-trash.ru',
	'no-ux.com',
	'noblepioneer.com',
	'nobugmail.com',
	'nobulk.com',
	'nobuma.com',
	'noclickemail.com',
	'nocp.ru',
	'nocp.store',
	'nodejs.uk',
	'nodezine.com',
	'noexpire.top',
	'nogmailspam.info',
	'noicd.com',
	'nokdot.com',
	'nokiamail.com',
	'nolemail.ga',
	'nolettersbox.com',
	'nomail.cf',
	'nomail.ga',
	'nomail.pw',
	'nomail2me.com',
	'nomes.fr.nf',
	'nomorespamemails.com',
	'nonchalantresmita.biz',
	'nongnue.com',
	'nonspam.eu',
	'nonspammer.de',
	'nonze.ro',
	'nooploop.store',
	'noopmail.com',
	'noref.in',
	'noreply.fr',
	'nori24.tv',
	'norseforce.com',
	'northsixty.com',
	'norwegischlernen.info',
	'nospam.fr.nf',
	'nospam.today',
	'nospam4.us',
	'nospamfor.us',
	'nospamthanks.info',
	'notboxletters.com',
	'nothingtoseehere.ca',
	'notif.me',
	'notipr.com',
	'notlettersmail.com',
	'notmailinator.com',
	'notrnailinator.com',
	'notsharingmy.info',
	'novaemail.com',
	'now.im',
	'nowhere.org',
	'nowmymail.com',
	'nowmymail.net',
	'noyp.fr.nf',
	'nproxi.com',
	'nqmo.com',
	'nrehi.com',
	'nrlord.com',
	'ns01.biz',
	'nsvpn.com',
	'nthrl.com',
	'ntlhelp.net',
	'nubescontrol.com',
	'nucleant.org',
	'nullbox.info',
	'nuoifb.com',
	'nuox.eu.org',
	'nurfuerspam.de',
	'nut.cc',
	'nutpa.net',
	'nuts2trade.com',
	'nvhrw.com',
	'nwldx.com',
	'nwytg.com',
	'nwytg.net',
	'ny7.me',
	'nyasan.com',
	'nyfhk.com',
	'nypato.com',
	'nyrmusic.com',
	'o2stk.org',
	'o7i.net',
	'oakon.com',
	'oalsp.com',
	'obfusko.com',
	'objectmail.com',
	'obobbo.com',
	'oborudovanieizturcii.ru',
	'obxpestcontrol.com',
	'octovie.com',
	'odaymail.com',
	'odeask.com',
	'odem.com',
	'odnorazovoe.ru',
	'oepia.com',
	'oerpub.org',
	'ofanda.com',
	'offshore-proxies.net',
	'ofisher.net',
	'ogrencikredisi.org',
	'ohaaa.de',
	'ohi.tw',
	'ohm.edu.pl',
	'oida.icu',
	'oing.cf',
	'okcdeals.com',
	'okclprojects.com',
	'okhko.com',
	'okiae.com',
	'okinawa.li',
	'okrent.us',
	'oku.ovh',
	'oky.ovh',
	'okzk.com',
	'olimp-case.ru',
	'oliviadiffuser.store',
	'oloh.ru',
	'oloh.store',
	'olypmall.ru',
	'omail.pro',
	'omarnasrrr.com',
	'omfg.run',
	'omnievents.org',
	'omtecha.com',
	'onbap.com',
	'one-mail.top',
	'one-ml.com',
	'one-sec-mail.site',
	'one-time.email',
	'one2mail.info',
	'onekisspresave.com',
	'onelettersmail.com',
	'onemail.host',
	'oneoffemail.com',
	'oneoffmail.com',
	'onetm-ml.com',
	'onetm.jp',
	'onetopclick.online',
	'onewaymail.com',
	'onheb.com',
	'onionred.com',
	'onlatedotcom.info',
	'online.ms',
	'onlineidea.info',
	'onlyapp.net',
	'onmailflare.com',
	'onqin.com',
	'ontasa.com',
	'ontyne.biz',
	'oohioo.com',
	'oolus.com',
	'oonies-shoprus.ru',
	'oooooooo.store',
	'oopi.org',
	'oosln.com',
	'oovk.ru',
	'oovk.store',
	'opayq.com',
	'opemails.com',
	'openavz.com',
	'opendns.ro',
	'openmail.pro',
	'opentrash.com',
	'opmmedia.ga',
	'oposite.org',
	'opp24.com',
	'optimaweb.me',
	'optivex.cfd',
	'opwebw.com',
	'oranek.com',
	'ordinaryamerican.net',
	'ordite.com',
	'oreidresume.com',
	'orgmbx.cc',
	'oroki.de',
	'oronny.com',
	'orsbap.com',
	'ortogenmail.com',
	'oshietechan.link',
	'oskarstalbergcitygenerator.com',
	'ostahie.com',
	'ostinmail.com',
	'osxofulk.com',
	'otherinbox.com',
	'otmail.jp',
	'ourklips.com',
	'ourpreviewdomain.com',
	'outlawspam.com',
	'outlook.edu.pl',
	'outmail.win',
	'ovbest.com',
	'ovomail.co',
	'ovpn.to',
	'owleyes.ch',
	'owlpic.com',
	'ownsyou.de',
	'owube.com',
	'oxfo.edu.pl',
	'oxmail.homes',
	'oxopoha.com',
	'ozatvn.com',
	'ozm.fr',
	'ozsaip.com',
	'ozyl.de',
	'p-aac.top',
	'p-banlis.ru',
	'p-ttz.top',
	'p33.org',
	'p71ce1m.com',
	'pa9e.com',
	'pachilly.com',
	'packiu.com',
	'pagamenti.tk',
	'paharpurmim.ga',
	'pakadebu.ga',
	'paliospiti.com',
	'pamaweb.com',
	'pamil.fr.nf',
	'pancakemail.com',
	'papierkorb.me',
	'paplease.com',
	'para2019.ru',
	'parlimentpetitioner.tk',
	'pastebitch.com',
	'pastryofistanbul.com',
	'patity.com',
	'patonce.com',
	'pavilionx2.com',
	'paxlys.com',
	'paylaar.com',
	'payperex2.com',
	'payspun.com',
	'pazard.com',
	'pazuric.com',
	'pckage.com',
	'pdf-cutter.com',
	'pe.hu',
	'peakinbox.net',
	'pecinan.com',
	'pecinan.net',
	'pecinan.org',
	'penisgoes.in',
	'penoto.tk',
	'pepamail.com',
	'pepbot.com',
	'percyfx.com',
	'pertera.com',
	'peterdethier.com',
	'petloca.com',
	'petrzilka.net',
	'pewpewpewpew.pw',
	'pflege-schoene-haut.de',
	'pfui.ru',
	'phamay.com',
	'phanmembanhang24h.com',
	'philipdowney.com',
	'phimib.com',
	'phmail.site',
	'phone-elkey.ru',
	'photo-impact.eu',
	'photobrex.com',
	'photomark.net',
	'phucmmo.com',
	'phyones.com',
	'physcroenmail.com',
	'physicaladithama.io',
	'pi.vu',
	'piaa.me',
	'picdirect.net',
	'pickuplanet.com',
	'pig.pp.ua',
	'pigeonprotocol.com',
	'pii.at',
	'piki.si',
	'pimpedupmyspace.com',
	'pinehill-seattle.org',
	'pingddns.net',
	'pingir.com',
	'pinkgreengenerator.me',
	'pipemail.space',
	'pippoc.com',
	'pisls.com',
	'pitaniezdorovie.ru',
	'pithu.org',
	'pitiful.pp.ua',
	'pivo-bar.ru',
	'pixdd.com',
	'pixelgagnant.net',
	'pixiil.com',
	'pixoledge.net',
	'pizu.ru',
	'pizu.store',
	'pizzajunk.com',
	'pjjkp.com',
	'placebomail10.com',
	'pleasenoham.org',
	'plexfirm.com',
	'plexolan.de',
	'plhk.ru',
	'pliz.fr.nf',
	'ploae.com',
	'ploki.fr',
	'ploncy.com',
	'plw.me',
	'pmail.site',
	'pngrise.com',
	'pochtac.ru',
	'poehali-otdihat.ru',
	'pofmagic.com',
	'pojok.ml',
	'pokemail.net',
	'pokemons1.fr.nf',
	'pokiemobile.com',
	'polarkingxx.ml',
	'politikerclub.de',
	'polkaroad.net',
	'polyfaust.net',
	'pongpong.org',
	'ponp.be',
	'pooae.com',
	'poofy.org',
	'pookmail.com',
	'pooo.ooguy.com',
	'poopiebutt.club',
	'popcornfarm7.com',
	'popcornfly.com',
	'popesodomy.com',
	'popgx.com',
	'poplk.com',
	'popol.fr.nf',
	'porjoton.com',
	'porsh.net',
	'porsilapongo.cl',
	'portaltrendsarena.com',
	'posdz.com',
	'posta.store',
	'postacin.com',
	'postbx.ru',
	'postbx.store',
	'postonline.me',
	'poubelle-du.net',
	'poubelle.fr.nf',
	'poutineyourface.com',
	'powered.name',
	'powerencry.com',
	'powerscrews.com',
	'powlearn.com',
	'pox2.com',
	'pp7rvv.com',
	'ppetw.com',
	'pptrvv.com',
	'pqoia.com',
	'pratikmail.com',
	'pratikmail.net',
	'pratikmail.org',
	'prazdnik-37.ru',
	'prc.cx',
	'predatorrat.cf',
	'predatorrat.ga',
	'predatorrat.gq',
	'predatorrat.ml',
	'predatorrat.tk',
	'premiapp.com',
	'premigu.co',
	'premium-mail.fr',
	'pretreer.com',
	'pricegh.com',
	'pricegh.fun',
	'pride-worldwi.de',
	'primabananen.net',
	'prin.be',
	'printz.site',
	'privacy-mail.top',
	'privacy.net',
	'privacyshield.cc',
	'privatdemail.net',
	'privmail.edu.pl',
	'privy-mail.com',
	'privy-mail.de',
	'privymail.de',
	'priyo-mail.com',
	'priyo.ovh',
	'priyo.site',
	'priyoemail.site',
	'priyomail.in',
	'priyomail.net',
	'priyomail.top',
	'priyomail.uk',
	'priyomail.us',
	'priyor.com',
	'priyp.com',
	'pro-tag.org',
	'pro5g.com',
	'procrackers.com',
	'profast.top',
	'prohade.com',
	'prohisi.store',
	'projectcl.com',
	'promailt.com',
	'proprietativalcea.ro',
	'propscore.com',
	'protectsmail.net',
	'protempmail.com',
	'protonza.com',
	'prout.be',
	'proxy-gateway.net',
	'proxymail.eu',
	'proxyparking.com',
	'prtnx.com',
	'prtshr.com',
	'prtz.eu',
	'psh.me',
	'psles.com',
	'psnator.com',
	'psoxs.com',
	'ptct.net',
	'ptsculure.com',
	'puabook.com',
	'puglieisi.com',
	'puji.pro',
	'punkass.com',
	'punkproof.com',
	'puppetmail.de',
	'purcell.email',
	'purelogistics.org',
	'pursip.com',
	'pushcom.store',
	'pusmail.com',
	'pussport.com',
	'put2.net',
	'puttanamaiala.tk',
	'putthisinyourspamdatabase.com',
	'pwpwa.com',
	'pwrby.com',
	'pxlys.com',
	'pyskillsgame.com',
	'qabq.com',
	'qacmjeq.com',
	'qasti.com',
	'qbfree.us',
	'qc.to',
	'qejjyl.com',
	'qibl.at',
	'qiott.com',
	'qipmail.net',
	'qiq.us',
	'qisdo.com',
	'qisoa.com',
	'qmrbe.com',
	'qodiq.com',
	'qoika.com',
	'qopow.com',
	'qq.my',
	'qsl.ro',
	'qtum-ico.com',
	'quadrafit.com',
	'questtechsystems.com',
	'quick-mail.cc',
	'quickemail.info',
	'quickinbox.com',
	'quickmail.nl',
	'quicksend.ch',
	'quillet.eu',
	'quinz.me',
	'quipas.com',
	'ququb.com',
	'qvmao.com',
	'qvy.me',
	'qwickmail.com',
	'qzueos.com',
	'r4nd0m.de',
	'ra3.us',
	'rabin.ca',
	'rabiot.reisen',
	'rabitex.com',
	'racaho.com',
	'rackabzar.com',
	'rael.us',
	'raetp9.com',
	'rainbowly.ml',
	'rajaiblis.com',
	'raketenmann.de',
	'raleigh-construction.com',
	'ramcen.com',
	'ramenmail.de',
	'ramin200.site',
	'ramizan.com',
	'rancidhome.net',
	'randol.infos.st',
	'randomail.io',
	'randomail.net',
	'rapidefr.fr.nf',
	'rapt.be',
	'raqid.com',
	'rawr.foo',
	'rax.la',
	'raxtest.com',
	'razemail.com',
	'razeny.com',
	'razuz.com',
	'rbb.org',
	'rbs1.xyz',
	'rcasd.com',
	'rcpt.at',
	'rdklcrv.xyz',
	're-gister.com',
	'reality-concept.club',
	'reallymymail.com',
	'realmka.io',
	'realquickemail.com',
	'realtyalerts.ca',
	'rebates.stream',
	'receiveee.com',
	'recipeforfailure.com',
	'recode.me',
	'reconmail.com',
	'recyclemail.dk',
	'redfeathercrow.com',
	'redi.fr.nf',
	'reftoken.net',
	'regapts.com',
	'regbypass.com',
	'regspaces.tk',
	'reimondo.com',
	'rejectmail.com',
	'rejo.technology',
	'rekaer.com',
	'reliable-mail.com',
	'remail.cf',
	'remail.ga',
	'remarkable.rocks',
	'remote.li',
	'rentaen.com',
	'renydox.com',
	'replyloop.com',
	'reportfresh.com',
	'reptilegenetics.com',
	'resgedvgfed.tk',
	'resmso.com',
	'reusable.email',
	'revolvingdoorhoax.org',
	'rezato.com',
	'rf.gd',
	'rfc822.org',
	'rhyta.com',
	'richfinances.pw',
	'riddermark.de',
	'rifkian.ga',
	'rimshacooking.site',
	'rinseart.com',
	'rippb.com',
	'risingsuntouch.com',
	'riski.cf',
	'risu.be',
	'rklips.com',
	'rkomo.com',
	'rm2rf.com',
	'rma.ec',
	'rmqkr.net',
	'rnailinator.com',
	'ro.lt',
	'robertspcrepair.com',
	'roborena.com',
	'robot-mail.com',
	'rodhazlitt.com',
	'roguemaster.dev',
	'rollindo.agency',
	'ronete.com',
	'ronnierage.net',
	'rootfest.net',
	'roratu.com',
	'rosebearmylove.ru',
	'rotaniliam.com',
	'rotomails.co.uk',
	'rotomails.com',
	'routerboardvietnam.com',
	'rover.info',
	'rowdydow.com',
	'rowe-solutions.com',
	'roweryo.com',
	'royal.net',
	'royaldoodles.org',
	'royalmarket.life',
	'royandk.com',
	'rppkn.com',
	'rr.nu',
	'rsvhr.com',
	'rteet.com',
	'rtrtr.com',
	'rtskiya.xyz',
	'rudymail.ml',
	'rulersonline.com',
	'rumgel.com',
	'runi.ca',
	'rupayamail.com',
	'ruru.be',
	'rustydoor.com',
	'rustyload.com',
	'ruu.kr',
	'rvb.ro',
	'rwstatus.com',
	'rygel.infos.st',
	'ryteto.me',
	'ryyr.ru',
	'ryyr.store',
	's0ny.net',
	's33db0x.com',
	's3k.net',
	'sabrestlouis.com',
	'sackboii.com',
	'saeoil.com',
	'safaat.cf',
	'safermail.info',
	'safersignup.de',
	'safetymail.info',
	'safetypost.de',
	'saharanightstempe.com',
	'sailmail.io',
	'sajutadollars.com',
	'saleis.live',
	'salmeow.tk',
	'sam1.eu.org',
	'samsclass.info',
	'sandcars.net',
	'sandelf.de',
	'sandwhichvideo.com',
	'sanfinder.com',
	'sanfnges.cc',
	'sanim.net',
	'sanstr.com',
	'sast.ro',
	'satisfyme.club',
	'satukosong.com',
	'sausen.com',
	'savests.com',
	'say0.com',
	'saynotospams.com',
	'scatmail.com',
	'scay.net',
	'schachrol.com',
	'schafmail.de',
	'schmeissweg.tk',
	'schrott-email.de',
	'scpulse.com',
	'scrsot.com',
	'sd3.in',
	'sdj.fr.nf',
	'sdvft.com',
	'sdvgeft.com',
	'sdvrecft.com',
	'search4gpt.com',
	'sebbcn.net',
	'secmail.pw',
	'secretemail.de',
	'secure-mail.biz',
	'secure-mail.cc',
	'secured-link.net',
	'securehost.com.es',
	'seedspeed.site',
	'seekapps.com',
	'seekjobs4u.com',
	'sehier.fr',
	'sejaa.lv',
	'selaciptama.com',
	'selfdestructingmail.com',
	'selfdestructingmail.org',
	'send22u.info',
	'send4.uk',
	'sendapp.uk',
	'sendfree.org',
	'sendingspecialflyers.com',
	'sendnow.win',
	'sendos.fr.nf',
	'sendos.infos.st',
	'sendspamhere.com',
	'senseless-entertainment.com',
	'seosnaps.com',
	'seqerc.com',
	'server.ms',
	'servicee.es',
	'services391.com',
	'servicetr.me',
	'setxko.com',
	'sexforswingers.com',
	'sexical.com',
	'sexyalwasmi.top',
	'sfolkar.com',
	'sgatra.com',
	'sgm.ovh',
	'shadap.org',
	'shalar.net',
	'sharedmailbox.org',
	'sharkfaces.com',
	'sharklasers.com',
	'shchiba.uk',
	'sheinup.com',
	'sheryli.com',
	'shhmail.com',
	'shhuut.org',
	'shieldedmail.com',
	'shieldemail.com',
	'shiftmail.com',
	'shipfromto.com',
	'shiphazmat.org',
	'shipping-regulations.com',
	'shippingterms.org',
	'shiptudo.com',
	'shitaway.tk',
	'shitmail.de',
	'shitmail.me',
	'shitmail.org',
	'shmeriously.com',
	'shopxda.com',
	'shortmail.net',
	'shotmail.ru',
	'showslow.de',
	'shrib.com',
	'shut.name',
	'shut.ws',
	'siberpay.com',
	'sicmg.com',
	'sidelka-mytischi.ru',
	'sidement.com',
	'siftportal.ru',
	'sify.com',
	'signalxp.com',
	'sihanoma.store',
	'sika3.com',
	'sikux.com',
	'silenceofthespam.com',
	'siliwangi.ga',
	'silvercoin.life',
	'sim-simka.ru',
	'simaenaga.com',
	'simpleitsecurity.info',
	'simranaitech.space',
	'sin.cl',
	'sinaite.net',
	'sind-hier.com',
	'sind-wir.com',
	'sindhier.com',
	'sindwir.com',
	'sinema.ml',
	'sinfiltro.cl',
	'singlespride.com',
	'sinnlos-mail.de',
	'sino.tw',
	'sinorto.com',
	'siteinfox.com',
	'siteposter.net',
	'six25.biz',
	'sizzlemctwizzle.com',
	'sjuaq.com',
	'skeefmail.com',
	'skrak.com',
	'skrx.tk',
	'sky-inbox.com',
	'sky-ts.de',
	'skygazerhub.com',
	'skynet.infos.st',
	'skyrt.de',
	'slapsfromlastnight.com',
	'slaskpost.se',
	'slave-auctions.net',
	'slippery.email',
	'slipry.net',
	'slopsbox.com',
	'slothmail.net',
	'slushmail.com',
	'slushpools.cloud',
	'sluteen.com',
	'sly.io',
	'smailpro.com',
	'smallker.tk',
	'smapfree24.com',
	'smapfree24.de',
	'smapfree24.eu',
	'smapfree24.info',
	'smapfree24.org',
	'smartemailbox.co',
	'smartnator.com',
	'smarttalent.pw',
	'smarttickethub.com',
	'smashmail.de',
	'smellfear.com',
	'smellrear.com',
	'smellypotato.tk',
	'smeux.com',
	'smkanba.com',
	'smncloud.com',
	'smtp99.com',
	'smwg.info',
	'smykwb.com',
	'snakebutt.com',
	'snakemail.com',
	'snapbx.com',
	'snapmail.cc',
	'snapmail.site',
	'snapwet.com',
	'sneakmail.de',
	'snece.com',
	'soc123.net',
	'social-mailer.tk',
	'socialfurry.org',
	'sociallymediocre.com',
	'soco7.com',
	'socoori.com',
	'sofia.re',
	'sofimail.com',
	'sofort-mail.de',
	'sofortmail.de',
	'sofrge.com',
	'softkey-office.ru',
	'softpls.asia',
	'sogetthis.com',
	'sohai.ml',
	'sohus.cn',
	'soioa.com',
	'soisz.com',
	'solar-impact.pro',
	'solarnyx.com',
	'solarunited.net',
	'solarunited.org',
	'solerbe.net',
	'solkill.store',
	'solvemail.info',
	'solventtrap.wiki',
	'somelora.com',
	'somoj.com',
	'songsign.com',
	'sonjj.edu.pl',
	'sonny.tk',
	'sonphuongthinh.com',
	'sonshi.cf',
	'soodmail.com',
	'soodomail.com',
	'soodonims.com',
	'soombo.com',
	'soon.it',
	'soscandia.org',
	'soyboy.observer',
	'sozenit.com',
	'spacebazzar.ru',
	'spacehotline.com',
	'spam-be-gone.com',
	'spam.care',
	'spam.ceo',
	'spam.la',
	'spam.org.es',
	'spam.su',
	'spam4.me',
	'spamail.de',
	'spamarrest.com',
	'spamavert.com',
	'spambob.com',
	'spambob.net',
	'spambob.org',
	'spambog.com',
	'spambog.de',
	'spambog.net',
	'spambog.ru',
	'spambooger.com',
	'spambox.info',
	'spambox.me',
	'spambox.org',
	'spambox.us',
	'spamcero.com',
	'spamcon.org',
	'spamcorptastic.com',
	'spamcowboy.com',
	'spamcowboy.net',
	'spamcowboy.org',
	'spamday.com',
	'spamdecoy.net',
	'spamex.com',
	'spamfellas.com',
	'spamfighter.cf',
	'spamfighter.ga',
	'spamfighter.gq',
	'spamfighter.ml',
	'spamfighter.tk',
	'spamfree.eu',
	'spamfree24.com',
	'spamfree24.de',
	'spamfree24.eu',
	'spamfree24.info',
	'spamfree24.net',
	'spamfree24.org',
	'spamgoes.in',
	'spamherelots.com',
	'spamhereplease.com',
	'spamhole.com',
	'spamify.com',
	'spaminator.de',
	'spamkill.info',
	'spaml.com',
	'spaml.de',
	'spamlot.net',
	'spammer.fail',
	'spammotel.com',
	'spammy.host',
	'spamobox.com',
	'spamoff.de',
	'spamsalad.in',
	'spamsandwich.com',
	'spamslicer.com',
	'spamsphere.com',
	'spamspot.com',
	'spamstack.net',
	'spamthis.co.uk',
	'spamthis.network',
	'spamthisplease.com',
	'spamtrail.com',
	'spamtrap.ro',
	'spamtroll.net',
	'spamwc.cf',
	'spamwc.ga',
	'spamwc.gq',
	'spamwc.ml',
	'sparkletoc.com',
	'speedgaus.net',
	'sperma.cf',
	'spicysoda.com',
	'spikio.com',
	'spindl-e.com',
	'spinly.net',
	'splitparents.com',
	'spoofmail.de',
	'sportrid.com',
	'spr.io',
	'spritzzone.de',
	'spruzme.com',
	'spybox.de',
	'spymail.com',
	'spymail.one',
	'squizzy.de',
	'squizzy.net',
	'sroff.com',
	'sry.li',
	'ssi-bsn.infos.st',
	'ssoia.com',
	'sssig.one',
	'stacys.mom',
	'stanfordujjain.com',
	'starlight-breaker.net',
	'starmail.net',
	'starpower.space',
	'startfu.com',
	'startkeys.com',
	'statdvr.com',
	'stathost.net',
	'statiix.com',
	'stayhome.li',
	'steam-area.ru',
	'steambot.net',
	'stexsy.com',
	'stinkefinger.net',
	'stop-my-spam.cf',
	'stop-my-spam.com',
	'stop-my-spam.ga',
	'stop-my-spam.ml',
	'stop-my-spam.pp.ua',
	'stop-my-spam.tk',
	'stopspam.app',
	'storebanme.com',
	'storegmail.net',
	'storiqax.top',
	'storj99.com',
	'storj99.top',
	'streetwisemail.com',
	'stromox.com',
	'stuckmail.com',
	'studyhub.edu.pl',
	'stuffmail.de',
	'stufmail.com',
	'stumpfwerk.com',
	'stylist-volos.ru',
	'submic.com',
	'suburbanthug.com',
	'suckmyd.com',
	'sudern.de',
	'sueshaw.com',
	'suexamplesb.com',
	'suftwari.com',
	'suioe.com',
	'super-auswahl.de',
	'super.lgbt',
	'superblohey.com',
	'superbowlstarttime.org',
	'supergreatmail.com',
	'supermailer.jp',
	'superplatyna.com',
	'superrito.com',
	'supersave.net',
	'superstachel.de',
	'superyp.com',
	'supporttc.com',
	'suremail.info',
	'sushisalmon.online',
	'sute.jp',
	'svetims.com',
	'svip520.cn',
	'svk.jp',
	'svxr.org',
	'swagpapa.com',
	'sweetpotato.ml',
	'sweetxxx.de',
	'swift-mail.net',
	'swift10minutemail.com',
	'swsdz.com',
	'syinxun.com',
	'sylvannet.com',
	'symphonyresume.com',
	'synarca.com',
	'syncax.com',
	'syosetu.gq',
	'syujob.accountants',
	'szerz.com',
	'tacomail.de',
	'tafmail.com',
	'tafoi.gr',
	'tagara.infos.st',
	'taglead.com',
	'tagmymedia.com',
	'tagyourself.com',
	'taimb.com',
	'talemarketing.com',
	'talkinator.com',
	'talmetry.com',
	'tamttts.com',
	'tanlanav.com',
	'tanukis.org',
	'taobudao.com',
	'taohucom.store',
	'taomail.web.id',
	'tapchicuoihoi.com',
	'taphear.com',
	'tapi.re',
	'tartinemoi.com',
	'tarzanmail.cf',
	'tastmemail.com',
	'tastrg.com',
	'tatadidi.com',
	'tatsu.uk',
	'taugr.com',
	'taukah.com',
	'taxibmt.com',
	'taxibmt.net',
	'tb-on-line.net',
	'tbgroupconsultants.com',
	'tcwlm.com',
	'tcwlx.com',
	'tdtda.com',
	'teamtitan.co',
	'tech69.com',
	'techblast.ch',
	'techemail.com',
	'techgroup.me',
	'technoproxy.ru',
	'techusa.org',
	'teerest.com',
	'teewars.org',
	'tefl.ro',
	'teihu.com',
	'telecomix.pl',
	'teleg.eu',
	'telegmail.com',
	'teleworm.com',
	'teleworm.us',
	'teligmail.site',
	'telimail.online',
	'tellos.xyz',
	'telvetto.com',
	'temailz.com',
	'teml.net',
	'temp-link.net',
	'temp-mail.best',
	'temp-mail.cfd',
	'temp-mail.com',
	'temp-mail.de',
	'temp-mail.gg',
	'temp-mail.lol',
	'temp-mail.now',
	'temp-mail.org',
	'temp-mail.pp.ua',
	'temp-mail.ru',
	'temp-mails.com',
	'temp.skymeshdynamics.com',
	'tempail.com',
	'tempalias.com',
	'tempblockchain.com',
	'tempe-mail.com',
	'tempemail.biz',
	'tempemail.co.za',
	'tempemail.com',
	'tempemail.net',
	'tempemailgen.com',
	'tempemaill.com',
	'tempemailo.org',
	'tempinbox.co.uk',
	'tempinbox.com',
	'tempmail.best',
	'tempmail.cc',
	'tempmail.cn',
	'tempmail.co',
	'tempmail.de',
	'tempmail.email',
	'tempmail.eu',
	'tempmail.id.vn',
	'tempmail.ing',
	'tempmail.io.vn',
	'tempmail.it',
	'tempmail.ninja',
	'tempmail.plus',
	'tempmail.pp.ua',
	'tempmail.us',
	'tempmail.vip',
	'tempmail.world',
	'tempmail.ws',
	'tempmail101.com',
	'tempmail2.com',
	'tempmaildemo.com',
	'tempmailer.com',
	'tempmailer.de',
	'tempmailer.net',
	'tempmailfree.net',
	'tempmailo.com',
	'tempmailr.com',
	'tempmails.net',
	'tempmailyo.org',
	'tempomail.fr',
	'tempomail.org',
	'tempomailo.site',
	'temporalemail.org',
	'temporam.com',
	'temporam.online',
	'temporam.xin',
	'temporam.xyz',
	'temporarily.de',
	'temporarioemail.com.br',
	'temporarly.com',
	'temporary-mail.net',
	'temporaryemail.dpdns.org',
	'temporaryemail.net',
	'temporaryemail.us',
	'temporaryforwarding.com',
	'temporaryinbox.com',
	'temporarymailaddress.com',
	'tempp-mails.com',
	'temppppo.store',
	'tempr.email',
	'tempsky.com',
	'temptami.com',
	'tempthe.net',
	'tempymail.com',
	'tenhub.uk',
	'tensi.org',
	'tenull.com',
	'ternaklele.ga',
	'terre.infos.st',
	'test-infos.fr.nf',
	'testore.co',
	'testudine.com',
	'tevstart.com',
	'tgduck.com',
	'thaitudang.xyz',
	'thanksnospam.info',
	'thankyou2010.com',
	'thatim.info',
	'thc.st',
	'the23app.com',
	'theaviors.com',
	'thebearshark.com',
	'thecarinformation.com',
	'thechildrensfocus.com',
	'thecity.biz',
	'thecloudindex.com',
	'thediamants.org',
	'thedirhq.info',
	'theeyeoftruth.com',
	'thegrampians.net',
	'thehjhvj.ink',
	'thejoker5.com',
	'thelightningmail.net',
	'thelimestones.com',
	'thembones.com.au',
	'themegreview.com',
	'themostemail.com',
	'thereddoors.online',
	'theroyalweb.club',
	'thescrappermovie.com',
	'thespamfather.com',
	'theteastory.info',
	'thetechnext.net',
	'thex.ro',
	'thichanthit.com',
	'thichmmo.com',
	'thietbivanphong.asia',
	'thirifara.com',
	'thisisnotmyrealemail.com',
	'thismail.net',
	'thisurl.website',
	'thnikka.com',
	'thoas.ru',
	'thraml.com',
	'thrma.com',
	'throam.com',
	'thrott.com',
	'throwam.com',
	'throwawayemailaddress.com',
	'throwawaymail.com',
	'throwawaymail.pp.ua',
	'throya.com',
	'thrubay.com',
	'thshyo.org',
	'thulinh.net',
	'thunderbolt.science',
	'thunkinator.org',
	'thxmate.com',
	'tianmi.me',
	'tiapz.com',
	'tic.ec',
	'tidissajiiu.com',
	'tiffincrane.com',
	'tikanony.com',
	'tikmail.org',
	'tiksofi.uk',
	'tiktakgrab.com',
	'tiktakgrab.shop',
	'tiktakgrabber.com',
	'tilien.com',
	'timgiarevn.com',
	'timkassouf.com',
	'tinoza.org',
	'tinpho.com',
	'tinytimer.org',
	'tinyurl24.com',
	'tipsb.com',
	'tittbit.in',
	'tiv.cc',
	'tivo.camdvr.org',
	'tizi.com',
	'tkfb24h.com',
	'tkitc.de',
	'tlpn.org',
	'tmail.com',
	'tmail.edu.rs',
	'tmail.gg',
	'tmail.io',
	'tmail.link',
	'tmail.ws',
	'tmail1.org',
	'tmail2.org',
	'tmail3.com',
	'tmail3.org',
	'tmail4.org',
	'tmail5.org',
	'tmail6.com',
	'tmail9.com',
	'tmaile.net',
	'tmailinator.com',
	'tmailor.com',
	'tmailor.net',
	'tmails.net',
	'tmmbt.net',
	'tmpbox.net',
	'tmpemails.com',
	'tmpeml.com',
	'tmpeml.info',
	'tmpjr.me',
	'tmpmail.net',
	'tmpmail.org',
	'tmpmailtor.com',
	'tmpnator.live',
	'tmpx.sa.com',
	'tnbeta.com',
	'toaik.com',
	'toddsbighug.com',
	'tofeat.com',
	'tohru.org',
	'toiea.com',
	'tokem.co',
	'token.ro',
	'tokenmail.de',
	'tokmail.net',
	'tonaeto.com',
	'tonne.to',
	'tonymanso.com',
	'too.li',
	'toolbox.ovh',
	'toolnator.plus',
	'toomail.biz',
	'toon.ml',
	'top-shop-tovar.ru',
	'top101.de',
	'top1mail.ru',
	'top1post.ru',
	'topdatamaster.com',
	'topinrock.cf',
	'topmail2.com',
	'topmail2.net',
	'topofertasdehoy.com',
	'topranklist.de',
	'toprumours.com',
	'topvu.net',
	'tormail.org',
	'torrent411.fr.nf',
	'tospage.com',
	'toss.pw',
	'tosunkaya.com',
	'totallynotfake.net',
	'totalvista.com',
	'totesmail.com',
	'totoan.info',
	'totococo.fr.nf',
	'tourcc.com',
	'tozya.com',
	'tp-qa-mail.com',
	'tppp.one',
	'tppp.online',
	'tpwlb.com',
	'tqoai.com',
	'tqosi.com',
	'trackden.com',
	'tradermail.info',
	'tranceversal.com',
	'trangiabao.click',
	'trangzim.uk',
	'translateid.com',
	'trap-mail.de',
	'trash-amil.com',
	'trash-mail.at',
	'trash-mail.cf',
	'trash-mail.com',
	'trash-mail.de',
	'trash-mail.ga',
	'trash-mail.gq',
	'trash-mail.ml',
	'trash-mail.tk',
	'trash-me.com',
	'trash2009.com',
	'trash2010.com',
	'trash2011.com',
	'trashcanmail.com',
	'trashdevil.com',
	'trashdevil.de',
	'trashemail.de',
	'trashemails.de',
	'trashinbox.com',
	'trashmail.at',
	'trashmail.com',
	'trashmail.de',
	'trashmail.fr',
	'trashmail.gq',
	'trashmail.hu',
	'trashmail.io',
	'trashmail.me',
	'trashmail.net',
	'trashmail.org',
	'trashmail.se',
	'trashmail.ws',
	'trashmailer.com',
	'trashmailgenerator.de',
	'trashmails.com',
	'trashymail.com',
	'trashymail.net',
	'trasz.com',
	'travelistaworld.com',
	'trayna.com',
	'trbvm.com',
	'trbvn.com',
	'trbvo.com',
	'trend-maker.ru',
	'trfu.to',
	'trgfu.com',
	'trgovinanaveliko.info',
	'trialmail.de',
	'trickmail.net',
	'trillianpro.com',
	'triots.com',
	'trixtrux1.ru',
	'trolebrotmail.com',
	'trollproject.com',
	'tropicalbass.info',
	'tropovenamail.com',
	'trungtamtoeic.com',
	'truthfinderlogin.com',
	'tryalert.com',
	'tryninja.io',
	'trythe.net',
	'tryzoe.com',
	'tsderp.com',
	'tshirtsavvy.com',
	'tsmtp.org',
	'ttirv.org',
	'ttszuo.xyz',
	'tualias.com',
	'tuamaeaquelaursa.com',
	'tubeemail.com',
	'tumroc.net',
	'tuofs.com',
	'tupmail.com',
	'turoid.com',
	'turual.com',
	'turuma.com',
	'tutuapp.bid',
	'tvchd.com',
	'tverya.com',
	'tweet.fr.nf',
	'twinmail.de',
	'twitch.work',
	'twkly.ml',
	'twocowmail.net',
	'twoweirdtricks.com',
	'twzhhq.online',
	'txcct.com',
	'txen.de',
	'txtadvertise.com',
	'tyhe.ro',
	'tyldd.com',
	'tympe.net',
	'uacro.com',
	'uber-mail.com',
	'ubinert.com',
	'ubismail.net',
	'ubm.md',
	'ucche.us',
	'ucupdong.ml',
	'uemail99.com',
	'ufacturing.com',
	'ufokeuabmail.com',
	'ug.wtf',
	'uggsrock.com',
	'uguuchantele.com',
	'uhe2.com',
	'uhhu.ru',
	'uiemail.com',
	'uiu.us',
	'ujijima1129.gq',
	'uk.to',
	'ukm.ovh',
	'ultra.fyi',
	'ultrada.ru',
	'uma3.be',
	'umail.net',
	'umil.net',
	'undeadbank.com',
	'underseagolf.com',
	'undo.it',
	'unicodeworld.com',
	'unids.com',
	'unimark.org',
	'uniromax.com',
	'unit7lahaina.com',
	'universall.me',
	'unmail.ru',
	'unratito.com',
	'uooos.com',
	'uorak.com',
	'upc.infos.st',
	'upliftnow.com',
	'uplipht.com',
	'uploadnolimit.com',
	'upozowac.info',
	'upphim.net',
	'upsnab.net',
	'urfunktion.se',
	'urhen.com',
	'uroid.com',
	'us.af',
	'us.to',
	'usa.cc',
	'usa.edu.pl',
	'usako.net',
	'usbc.be',
	'usdtbeta.com',
	'used-product.fr',
	'uselesswebsites.net',
	'ushijima1129.cf',
	'ushijima1129.ga',
	'ushijima1129.gq',
	'ushijima1129.ml',
	'ushijima1129.tk',
	'usm.ovh',
	'ustorp.com',
	'utiket.us',
	'uu.gl',
	'uu2.ovh',
	'uue.edu.pl',
	'uuf.me',
	'uuii.in',
	'uwork4.us',
	'uyhip.com',
	'uz8.net',
	'v-mail.xyz',
	'vaasfc4.tk',
	'vaati.org',
	'vafyxh.com',
	'valanides.com',
	'valemail.net',
	'valhalladev.com',
	'vankin.de',
	'vasteron.com',
	'vaupk.org',
	'vbv.cards',
	'vcois.com',
	'vctel.com',
	'vda.ro',
	'vddaz.com',
	'vdig.com',
	'veanlo.com',
	'veb37.com',
	'veltexline.com',
	'vemomail.win',
	'venompen.com',
	'veo.kr',
	'ver0.cf',
	'ver0.ga',
	'ver0.gq',
	'ver0.ml',
	'ver0.tk',
	'vercelli.cf',
	'vercelli.ga',
	'vercelli.gq',
	'vercelli.ml',
	'verdejo.com',
	'vermutlich.net',
	'vertexinbox.com',
	'vertexium.net',
	'veruvercomail.com',
	'veryday.ch',
	'veryday.eu',
	'veryday.info',
	'veryrealemail.com',
	'ves.ink',
	'vesa.pw',
	'vetra.cyou',
	'vevs.de',
	'vexi.my',
	'via.tokyo.jp',
	'vibzi.net',
	'vickaentb.tk',
	'victime.ninja',
	'victoriaalison.com',
	'victoriantwins.com',
	'vidchart.com',
	'videotoptop.com',
	'viditag.com',
	'vidwobox.com',
	'viewcastmedia.com',
	'viewcastmedia.net',
	'viewcastmedia.org',
	'vigilantkeep.net',
	'vigoneo.com',
	'vikingsonly.com',
	'vinaemail.com',
	'vinakoop.com',
	'vinakop.com',
	'vinernet.com',
	'vintomaper.com',
	'viole.cfd',
	'vipepe.com',
	'vipmail.name',
	'vipmail.pw',
	'vips.pics',
	'vipxm.net',
	'viralplays.com',
	'virgilian.com',
	'virtualemail.info',
	'visal007.tk',
	'visal168.cf',
	'visal168.ga',
	'visal168.gq',
	'visal168.ml',
	'visal168.tk',
	'visignal.com',
	'vitahicks.com',
	'vitaspherelife.com',
	'viv2.com',
	'vixletdev.com',
	'vixtricks.com',
	'vjoid.ru',
	'vjoid.store',
	'vjuum.com',
	'vkbb.ru',
	'vkbb.store',
	'vkbt.ru',
	'vkbt.store',
	'vkcbt.ru',
	'vkcbt.store',
	'vkcode.ru',
	'vkfu.ru',
	'vkfu.store',
	'vkpr.store',
	'vkr1.com',
	'vkrr.ru',
	'vkrr.store',
	'vlemi.com',
	'vlrregulatory.com',
	'vmailing.info',
	'vmani.com',
	'vmpanda.com',
	'vn-one.com',
	'vncctv.org',
	'vnedu.me',
	'vobau.net',
	'voidbay.com',
	'volaj.com',
	'volku.org',
	'voltaer.com',
	'vomoto.com',
	'vorga.org',
	'votiputox.org',
	'voxelcore.com',
	'voxinh.net',
	'vpn.st',
	'vpnseat.com',
	'vps30.com',
	'vps911.net',
	'vradportal.com',
	'vremonte24-store.ru',
	'vrmtr.com',
	'vsimcard.com',
	'vsmailpro.com',
	'vssms.com',
	'vtxmail.us',
	'vubby.com',
	'vuiy.pw',
	'vuket.org',
	'vulca.sbs',
	'vusra.com',
	'vvatxiy.com',
	'vwhins.com',
	'vxsolar.com',
	'vztc.com',
	'w-asertun.ru',
	'w3internet.co.uk',
	'wacold.com',
	'waivey.com',
	'wakingupesther.com',
	'walala.org',
	'walkmail.net',
	'walkmail.ru',
	'wallm.com',
	'wanko.be',
	'wanva.shop',
	'warlus.asso.st',
	'waroengpremium.com',
	'warunkto.com',
	'watch-harry-potter.com',
	'watchever.biz',
	'watchfull.net',
	'watchironman3onlinefreefullmovie.com',
	'waterisgone.com',
	'watrf.com',
	'wazabi.club',
	'wbdev.tech',
	'wbml.net',
	'wearkeymail.site',
	'web-contact.info',
	'web-ideal.fr',
	'web-inc.net',
	'web-mail.pp.ua',
	'web2mailco.com',
	'webclub.infos.st',
	'webcontact-france.eu',
	'webemail.me',
	'webhook.site',
	'webinarmoa.com',
	'webm4il.info',
	'webmail24.top',
	'webofip.com',
	'webscash.com',
	'webstore.fr.nf',
	'webtrip.ch',
	'webuser.in',
	'wecp.ru',
	'wecp.store',
	'wee.my',
	'weekfly.com',
	'wef.gr',
	'weg-werf-email.de',
	'wegwerf-email-addressen.de',
	'wegwerf-email-adressen.de',
	'wegwerf-email.at',
	'wegwerf-email.de',
	'wegwerf-email.net',
	'wegwerf-emails.de',
	'wegwerfadresse.de',
	'wegwerfemail.com',
	'wegwerfemail.de',
	'wegwerfemail.info',
	'wegwerfemail.net',
	'wegwerfemail.org',
	'wegwerfemailadresse.com',
	'wegwerfmail.de',
	'wegwerfmail.info',
	'wegwerfmail.net',
	'wegwerfmail.org',
	'wegwerpmailadres.nl',
	'wegwrfmail.de',
	'wegwrfmail.net',
	'wegwrfmail.org',
	'weizixu.com',
	'wekawa.com',
	'welikecookies.com',
	'wellsfargocomcardholders.com',
	'wemel.top',
	'wenkuu.com',
	'wentcity.com',
	'wep.email',
	'wetrainbayarea.com',
	'wetrainbayarea.org',
	'wfgdfhj.tk',
	'wg0.com',
	'wh4f.org',
	'whaaaaaaaaaat.com',
	'whatagarbage.com',
	'whatiaas.com',
	'whatifanalytics.com',
	'whatpaas.com',
	'whatsaas.com',
	'whiffles.org',
	'whitehousecalculator.com',
	'whopy.com',
	'whyspam.me',
	'wibblesmith.com',
	'wickmail.net',
	'widaryanto.info',
	'widget.gg',
	'wiemei.com',
	'wierie.tk',
	'wifimaple.com',
	'wifioak.com',
	'wikfee.com',
	'wikidocuslava.ru',
	'wilemail.com',
	'willhackforfood.biz',
	'willselfdestruct.com',
	'wimsg.com',
	'winemaven.info',
	'winocs.com',
	'wins.com.br',
	'wir-sind.com',
	'wireconnected.com',
	'wishy.fr',
	'wivstore.com',
	'wlist.ro',
	'wlsom.com',
	'wmail.cf',
	'wmail.club',
	'wnbaldwy.com',
	'wokcy.com',
	'wolfmail.ml',
	'wolfsmail.tk',
	'wollan.info',
	'womp-wo.mp',
	'woofidog.fr.nf',
	'woopros.com',
	'workingtall.com',
	'worldlylife.store',
	'worldspace.link',
	'wpdork.com',
	'wpg.im',
	'wralawfirm.com',
	'writeme.us',
	'wronghead.com',
	'ws.gy',
	'wsym.de',
	'wsypc.com',
	'wudet.men',
	'wuespdj.xyz',
	'wupics.com',
	'wuuvo.com',
	'wuzak.com',
	'wuzup.net',
	'wuzupmail.net',
	'wwc8.com',
	'wwefd.top',
	'wwjmp.com',
	'wwvk.ru',
	'wwvk.store',
	'wwwnew.eu',
	'wxcv.fr.nf',
	'wxnw.net',
	'wyoxafp.com',
	'wywnxa.com',
	'wzofit.com',
	'x-lab.net',
	'x0q.net',
	'x1ix.com',
	'x24.com',
	'xagloo.co',
	'xagloo.com',
	'xapimail.top',
	'xbaby69.top',
	'xcode.ro',
	'xcodes.net',
	'xcompress.com',
	'xcoxc.com',
	'xcpy.com',
	'xcvv.xyz',
	'xehop.org',
	'xemaps.com',
	'xemne.com',
	'xenta.cfd',
	'xents.com',
	'xepa.ru',
	'xfavaj.com',
	'xidealx.com',
	'ximenor.site',
	'xjoi.com',
	'xkx.me',
	'xkxkud.com',
	'xl.cx',
	'xmailer.be',
	'xmailtm.com',
	'xmaily.com',
	'xn--9kq967o.com',
	'xn--d-bga.net',
	'xn--yaho-sqa.com',
	'xojxe.com',
	'xopmail.fun',
	'xost.us',
	'xoxox.cc',
	'xperiae5.com',
	'xrap.de',
	'xstyled.net',
	'xvx.us',
	'xww.ro',
	'xxhamsterxx.ga',
	'xxi2.com',
	'xxl.st',
	'xxlocanto.us',
	'xxolocanto.us',
	'xxqx3802.com',
	'xxvk.ru',
	'xxvk.store',
	'xxxhi.cc',
	'xy9ce.tk',
	'xylar.ru',
	'xylar.store',
	'xyzfree.net',
	'xzsok.com',
	'yabai-oppai.tk',
	'yabes.ovh',
	'yahmail.top',
	'yahooproduct.net',
	'yaloo.fr.nf',
	'yamail.win',
	'yanet.me',
	'yannmail.win',
	'yapped.net',
	'yaqp.com',
	'yarnpedia.ga',
	'ycare.de',
	'ycn.ro',
	'ye.vc',
	'yecp.ru',
	'yecp.store',
	'yedi.org',
	'yeezus.ru',
	'yep.it',
	'yermail.net',
	'yhg.biz',
	'ynmrealty.com',
	'yodx.ro',
	'yogamaven.com',
	'yoggm.com',
	'yogirt.com',
	'yomail.edu.pl',
	'yomail.info',
	'yomand.store',
	'yoo.ro',
	'yopmail.com',
	'yopmail.fr',
	'yopmail.gq',
	'yopmail.kro.kr',
	'yopmail.net',
	'yopmail.pp.ua',
	'yoptruc.fr.nf',
	'yordanmail.cf',
	'yotmail.fr.nf',
	'you-spam.com',
	'yougotgoated.com',
	'youmail.ga',
	'youmailr.com',
	'youneedmore.info',
	'youpymail.com',
	'your5.ru',
	'your5.store',
	'yourdomain.com',
	'youremail.cf',
	'yourewronghereswhy.com',
	'yourlms.biz',
	'yours.tools',
	'yourspamgoesto.space',
	'yourtube.ml',
	'youxiang.dev',
	'yroid.com',
	'yshdnhh.click',
	'yspend.com',
	'ytnhy.com',
	'ytpayy.com',
	'yugasandrika.com',
	'yui.it',
	'yuki.ren',
	'yun.pics',
	'yuoia.com',
	'yuurok.com',
	'ywzmb.top',
	'yxdad.ru',
	'yxdad.store',
	'yxzx.net',
	'yyolf.net',
	'yzcalo.com',
	'yzm.de',
	'z-o-e-v-a.ru',
	'z0d.eu',
	'z1p.biz',
	'z86.ru',
	'zain.com.co',
	'zain.site',
	'zainmax.net',
	'zaktouni.fr',
	'zarabotokdoma11.ru',
	'zarkbin.store',
	'zasod.com',
	'zaym-zaym.ru',
	'zcovz.ru',
	'zcovz.store',
	'zcrcd.com',
	'zdenka.net',
	'ze.tc',
	'zebins.com',
	'zebins.eu',
	'zehnminuten.de',
	'zehnminutenmail.de',
	'zemzar.net',
	'zenbyul.com',
	'zenthranet.com',
	'zepp.dk',
	'zeronerbacomail.com',
	'zeropolly.com',
	'zetmail.com',
	'zfymail.com',
	'zhaoqian.ninja',
	'zhaoyuanedu.cn',
	'zhcne.com',
	'zhewei88.com',
	'zhorachu.com',
	'zik.dj',
	'zipcad.com',
	'zipcatfish.com',
	'zipo1.gq',
	'zippymail.info',
	'zipsendtest.com',
	'zira.my',
	'ziragold.com',
	'zivox.sbs',
	'zizo7.com',
	'zmail.cam',
	'zoaxe.com',
	'zoemail.com',
	'zoemail.net',
	'zoemail.org',
	'zoetropes.org',
	'zombie-hive.com',
	'zomg.info',
	'zoomintens.com',
	'zorg.fr.nf',
	'zouz.fr.nf',
	'zozozo123.com',
	'zsero.com',
	'zudpck.com',
	'zumpul.com',
	'zv68.com',
	'zvvzuv.com',
	'zx81.ovh',
	'zxcv.com',
	'zxcvbnm.com',
	'zymuying.com',
	'zyns.com',
	'zzi.us',
	'zzrgg.com',
	'zzz.com',
]);

export const isDisposableEmailDomain = (email?: string): boolean => {
	const domain = email?.split('@')[1]?.trim().toLowerCase();
	return !!domain && DISPOSABLE_EMAIL_DOMAINS.has(domain);
};

export const disposableEmailValidation = emailValidation.test(
	'not-disposable',
	'Please use a permanent email address. Disposable addresses are not allowed.',
	(value) => !isDisposableEmailDomain(value)
);

export const capitalizeFirstLetters = (sentence = '') => {
	const words = sentence.split(' ');
	const capitalizedWords = words.map((word) => word[0].toUpperCase() + word.slice(1));
	const capitalizedSentence = capitalizedWords.join(' ');
	return capitalizedSentence;
};

export const GenerateBreadcrumb = (pathname = '') => {
	const segments = pathname.split('/').filter((segment) => segment);

	const breadcrumb: Breadcrumb[] = segments.map((segment, index) => {
		const segmentPath = `/${segments.slice(0, index + 1).join('/')}`;

		let segmentLabel = capitalizeFirstLetters(segment.replace(/-+/g, ' '));

		segmentLabel = segmentLabel.replace(/\bId\b/g, 'ID');

		return { label: segmentLabel, url: segmentPath };
	});

	return breadcrumb;
};

export const defaultFiatMetaProps: IFiatMetaProps = {
	country: 'Nigeria',
	countryLower: 'nigeria',
	currency: 'NGN',
	currencyLower: 'ngn',
	currencySymbol: CURRENCYCONFIG.NGN.symbol,
	currencyShort: 'naira',
};

export const capitalize = (word: string): string => {
	if (!word) return '';
	return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
};

export const getFiatPropsByCountry = (country: string): ICurrencyConfig | undefined =>
	Object.values(CURRENCYCONFIG).find((v) => v.country.toLowerCase() === country.toLowerCase());

export const getFxRateByCountry = (country: Lowercase<COUNTRYTYPE>, fxRates?: ISettingsFXRates) => {
	if (!fxRates) return 0;

	const rate = fxRates.NGNToUSDForVIP;

	if (country === 'ghana') {
		return rate / fxRates.NGNToGHS;
	}

	return rate;
};

export const setFiatMetaProps = (data: IApiResponse<IUser>): IFiatMetaProps => {
	const userData = data.data;
	const fxRates = data.meta?.settings.fxRates;
	const fiatProps = getFiatPropsByCountry(userData?.country ?? '');

	if (!userData || !fiatProps || !fxRates) return defaultFiatMetaProps;

	const countryLowerCase = userData.country.toLowerCase() as Lowercase<COUNTRYTYPE>;

	return {
		country: capitalize(userData.country) as COUNTRYTYPE,
		countryLower: countryLowerCase,
		currency: fiatProps.code as CURRENCYCODETYPE,
		currencyLower: fiatProps.code.toLowerCase() as Lowercase<CURRENCYCODETYPE>,
		currencySymbol: fiatProps.symbol as CURRENCYSYMBOLTYPE,
		currencyShort: fiatProps.currencyShort as CURRENCYSHORTTYPE,
	};
};

export const getInitials = (str: string): string => (str.match(/[A-Z]/g) ?? []).join('').toLowerCase();

export const otpValidation = yup.string().required('OTP is required').trim().matches(/^\d+$/, 'OTP must contain only numbers');

export const OTPVerificationSchema = yup.object({
	otp: otpValidation,
});

export const mapBankToDropDownItems = (item: IBank): IDropDownItem => ({
	code: getInitials(item.name),
	icon: item.avatar,
	name: item.name,
	value: item.name,
	id: item.id,
});

export interface IPhoneInputDropDown {
	searchTerm: string;
	handleSearchTerm?: (e: ChangeEvent<HTMLInputElement>) => void;
	handleCountrySelect?: (country: CountryProps) => (e: MouseEvent<HTMLButtonElement>) => void;
	selectedCountry: CountryProps;
}

export interface PhoneInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'placeholder'> {
	value?: string;
	placeholder?: string;
	defaultCountry?: string;
	label?: string;
	onChange?: (phone: string) => void;
	errorMessage?: string;
	hideSearch?: boolean;
	disableDropdown?: boolean;
}

export const formatDateTime = (isoString: string | undefined) => {
	const date = new Date(isoString ?? '');

	const dateStr = date.toLocaleDateString('en-US', {
		year: 'numeric',
		month: 'short',
		day: 'numeric',
	});

	const timeStr = date.toLocaleTimeString('en-US', {
		hour: 'numeric',
		minute: '2-digit',
		second: '2-digit',
	});

	return `${dateStr}, ${timeStr}`;
};

export const formatDateAndTime = (isoString: string) => {
	const dateObj = new Date(isoString);

	const date = dateObj.toLocaleDateString('en-US', {
		month: 'long',
		day: 'numeric',
		year: 'numeric',
	});

	const time = dateObj.toLocaleTimeString('en-US', {
		hour: 'numeric',
		minute: '2-digit',
		second: '2-digit',
		hour12: true,
	});

	return { date, time };
};

export const ResetPasswordOTPSchema = yup.object({
	otp: otpValidation,
});

/**
 * Synchronous, non-reversible hash (cyrb53) used to derive an opaque equality tag
 * from a value without persisting it in readable form — e.g. tagging the stored
 * "last visited" route with *whose* it is, so a different user can't inherit it,
 * without writing their email to localStorage. This is an equality tag, NOT a
 * security/crypto boundary.
 */
export const hashIdentifier = (value: string): string => {
	const normalized = value.trim().toLowerCase();
	let h1 = 0xdeadbeef;
	let h2 = 0x41c6ce57;

	for (let i = 0; i < normalized.length; i++) {
		const ch = normalized.charCodeAt(i);
		h1 = Math.imul(h1 ^ ch, 2654435761);
		h2 = Math.imul(h2 ^ ch, 1597334677);
	}

	h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
	h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);

	return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(36);
};

export const copyToClipboard = (text?: string, message?: string) => async () => {
	try {
		if (!text) throw new Error('');
		await navigator.clipboard.writeText(text);
		showToast('success', message ?? 'copied');
		return true;
	} catch (error) {
		console.error('Failed to copy text:', error);
		showToast('error', 'Failed to copy text');
		return false;
	}
};

export const shareOrCopyText =
	({ title, text, url, copyMessage }: { title?: string; text?: string; url?: string; copyMessage?: string }) =>
	async () => {
		const shareData: ShareData = { title, text, ...(url ? { url } : {}) };

		try {
			if ('share' in navigator && navigator.canShare(shareData)) {
				await navigator.share(shareData);
				return true;
			}
		} catch (error) {
			if ((error as Error).name === 'AbortError') return false;
		}

		const clipboardText = [text, url].filter(Boolean).join('\n\n');
		return copyToClipboard(clipboardText, copyMessage ?? 'Details copied')();
	};

export const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

export const copyText = async (text: string, message?: string): Promise<boolean> => {
	try {
		if (!text) throw new Error('');

		if (window.isSecureContext) {
			await navigator.clipboard.writeText(text);
			showToast('success', message ?? 'copied');
			return true;
		}

		const ta = document.createElement('textarea');
		ta.value = text;
		ta.setAttribute('readonly', '');
		ta.style.position = 'fixed';
		ta.style.top = '0';
		ta.style.left = '-9999px';
		ta.style.opacity = '0';
		document.body.appendChild(ta);
		ta.focus();
		ta.select();

		const copyHint = isMobile ? 'Tap and hold to copy.' : 'Press Ctrl/Cmd+C to copy.';
		showToast('error', `Automatic copy not available. ${copyHint}`);

		setTimeout(() => {
			if (document.body.contains(ta)) {
				document.body.removeChild(ta);
			}
		}, 5000);

		return false;
	} catch (error) {
		console.error('Failed to copy text:', error);
		showToast('error', 'Failed to copy text');
		return false;
	}
};

export const getLast7Days = (): DateRange => {
	const end = new Date();
	end.setUTCHours(0, 0, 0, 0);

	const start = new Date(end);
	start.setDate(end.getDate() - 6);

	return {
		startDate: start.toISOString(),
		endDate: end.toISOString(),
	};
};

export const formatToTime = (dateStr: string): string => {
	const date = new Date(dateStr);
	return date.toLocaleTimeString('en-US', {
		hour: '2-digit',
		minute: '2-digit',
		hour12: true,
	});
};

export const formatDateToReadable = (dateStr: string): string => {
	const date = new Date(dateStr);
	return new Intl.DateTimeFormat('en-US', {
		month: 'short',
		day: '2-digit',
		year: 'numeric',
	})
		.format(date)
		.toUpperCase();
};

export const isValidClass = (className?: string) => (className ? ` ${className}` : '');

export const mapPhoneCountryToDropDownItems = (item: CountryProps): IDropDownItem => ({
	code: item.code,
	icon: item.flag,
	name: item.name,
	value: item.dialCode,
	id: item.code,
});

export const mapCountryToDropDownItems = (item: CountryProps): IDropDownItem => ({
	code: item.code,
	icon: item.flag,
	name: item.name,
	value: item.name,
	id: item.name,
});

export const hideComponent = (windowSize: windowSizeType, screen: 'desktop' | 'mobile', size = 768) => {
	if (!windowSize.isClient) return { hideColumn: false };

	const isMobileArg = windowSize.width <= size;
	return { hideColumn: screen === 'desktop' ? !isMobileArg : isMobileArg };
};

export const getTransactionStatus = (status: ITransactionStatus) => {
	const obj: Record<ITransactionStatus, IAppStatus> = {
		completed: 'successful',
		deleted: 'unsuccessful',
		failed: 'unsuccessful',
		flagged: 'flagged',
		pending: 'pending',
		processing: 'processing',
		reversed: 'reversed',
		rejected: 'unsuccessful',
	};

	return obj[status];
};

export const generateUEID = (): string => {
	const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
	const bytes = new Uint8Array(8);
	crypto.getRandomValues(bytes);

	return Array.from(bytes, (b) => chars[b % chars.length]).join('');
};

export const getMagnitudeText = (value: string | number): string => {
	const cleanedValue = String(value).replace(/[^0-9.-]/g, '');
	const num = Math.abs(parseFloat(cleanedValue));

	const ranges: { min: number; text: string }[] = [
		{ min: 100_000_000_000_000, text: 'Hundreds of Trillions' },
		{ min: 10_000_000_000_000, text: 'Tens of Trillions' },
		{ min: 1_000_000_000_000, text: 'Trillions' },
		{ min: 100_000_000_000, text: 'Hundreds of Billions' },
		{ min: 10_000_000_000, text: 'Tens of Billions' },
		{ min: 1_000_000_000, text: 'Billions' },
		{ min: 100_000_000, text: 'Hundreds of Millions' },
		{ min: 10_000_000, text: 'Tens of Millions' },
		{ min: 1_000_000, text: 'Millions' },
		{ min: 100_000, text: 'Hundreds of Thousands' },
		{ min: 10_000, text: 'Tens of Thousands' },
		{ min: 1_000, text: 'Thousands' },
		{ min: 100, text: 'Hundreds' },
		{ min: 10, text: 'Tens' },
		{ min: 1, text: 'Units' },
	];

	return Number.isNaN(num) || num === 0 ? '' : (ranges.find((range) => num >= range.min)?.text ?? '');
};

export const formatCurrencyInputAmount = (
	amount: string | number,
	currencyCode: CURRENCYCODETYPE,
	fractionDigits = 2
): string => {
	const amountStr = String(amount);
	const endsWithDot = amountStr.endsWith('.');

	const { locale } = getSafeObjectField(CURRENCYCONFIG, currencyCode, CURRENCYCONFIG.USD);
	let numericAmount: number;

	if (typeof amount === 'string') {
		const cleanedAmount = amount.replace(/[^\d.-]/g, '');
		numericAmount = parseFloat(cleanedAmount);
	} else {
		numericAmount = amount;
	}

	if (Number.isNaN(numericAmount)) {
		return '0';
	}

	const formattedAmount = numericAmount.toLocaleString(locale, {
		style: 'decimal',
		maximumFractionDigits: fractionDigits,
	});

	if (endsWithDot) {
		const decimalSeparator = (1.1).toLocaleString(locale).charAt(1);

		if (!formattedAmount.includes(decimalSeparator)) {
			return `${formattedAmount}${decimalSeparator}`;
		}
	}

	return formattedAmount;
};

export const formatCurrencyAmount = (amount: string | number, currencyCode: CURRENCYCODETYPE, fractionDigits = 2) => {
	const { locale, country } = getSafeObjectField(CURRENCYCONFIG, currencyCode, CURRENCYCONFIG.USD);

	let numericAmount: number;

	if (typeof amount === 'string') {
		const cleanedAmount = amount.replace(/[^\d.-]/g, '');
		numericAmount = parseFloat(cleanedAmount);
	} else {
		numericAmount = amount;
	}

	if (Number.isNaN(numericAmount)) {
		return `0.${'0'.repeat(fractionDigits)}`;
	}

	let formattedAmount = numericAmount.toLocaleString(locale, {
		style: 'currency',
		currency: currencyCode,
		minimumFractionDigits: fractionDigits,
		maximumFractionDigits: fractionDigits,
	});

	if (country === 'Ghana') {
		formattedAmount = formattedAmount.replace('GH₵', '₵');
	}

	return formattedAmount;
};

export const getWalletProps = (currencyCode: CURRENCYCODETYPE | undefined, userWallet: IUserWallet[]): IWalletProps => {
	try {
		if (!currencyCode) throw new Error('currency code invalid');

		if (!Array.isArray(userWallet) || !userWallet[0]) throw new Error('wallet empty');

		const wallet = userWallet.find(
			(walletItem) => walletItem.type === 'fiat' && walletItem.currency === currencyCode.toLowerCase()
		);

		if (!wallet?.userInfo) throw new Error('wallet not found');

		const currencyAmount = formatCurrencyAmount(wallet.balance, currencyCode);

		return {
			currencyAmount,
			walletBalance: wallet.balance,
			walletId: wallet._id,
			name: wallet.userInfo.username,
			email: wallet.userInfo.email,
			symbol: getSafeObjectField(CURRENCYCONFIG, currencyCode, CURRENCYCONFIG.USD).symbol,
			code: getSafeObjectField(CURRENCYCONFIG, currencyCode, CURRENCYCONFIG.USD).code,
		};
	} catch (error) {
		console.error(error);

		return {
			currencyAmount: '0',
			walletBalance: 0,
			walletId: '',
			name: '',
			email: '',
			symbol: '',
			code: '',
		};
	}
};

export const getCurrencyProps = (user: IUser | undefined): CURRENCYCONFIGTYPE | undefined => {
	try {
		if (!user) throw new Error('userdata is not found');

		const isNgN = user.country === 'nigeria';

		const currencyCode: CURRENCYCODETYPE = isNgN ? 'NGN' : 'GHS';

		return getSafeObjectField(CURRENCYCONFIG, currencyCode, CURRENCYCONFIG.USD);
	} catch (error) {
		console.error(error);

		return undefined;
	}
};

export const getCleanedValue = (valueArg?: string) => {
	if (!valueArg) return '';

	// Drop grouping separators and whitespace ("1,234.56" -> "1234.56"), then keep only the
	// leading run of digits/decimal points. Truncating (rather than globally stripping) means
	// intent-altering input collapses to a visibly-wrong prefix the user will re-check instead of
	// a plausible-but-wrong number: "1e3" -> "1" (never "13"), "-1" -> "1", "1.5e3" -> "1.5".
	const stripped = valueArg.replace(/[,\s]/g, '');
	const match = /^[^\d.]*([\d.]*)/.exec(stripped);
	return match ? match[1] : '';
};

export const handleDecimalPoints = (cleanedValueArg: string) => {
	const decimalIndex = cleanedValueArg.indexOf('.');
	let cleanedValue = cleanedValueArg;
	if (decimalIndex !== -1) {
		cleanedValue = cleanedValue.substring(0, decimalIndex + 1) + cleanedValue.substring(decimalIndex + 1).replace(/\./g, '');
	}
	return cleanedValue;
};

const getWithdrawRulesAmount = (
	settings: ISettings,
	fromCurrency: CURRENCYCODETYPE | undefined,
	minNGNAmount: number,
	minGHSAmount: number
) => {
	const isUsd = fromCurrency === 'USD';
	const isNgn = fromCurrency === 'NGN';

	const withdrawalMinFiatAmount = isNgn ? minNGNAmount : minGHSAmount;

	const withdrawalMinAmount = isUsd ? settings.usdWithdrawalMeta?.vip.minimum : withdrawalMinFiatAmount;

	const withdrawalMaxFiatAmount = isNgn
		? settings.withdrawalMeta.vip.withdrawal.maximum.nigeria
		: settings.withdrawalMeta.vip.withdrawal.maximum.ghana;

	const withdrawalMaxAmount = isUsd ? settings.usdWithdrawalMeta?.vip.maximum : withdrawalMaxFiatAmount;

	return {
		withdrawalMaxAmount: withdrawalMaxAmount ?? 0,
		withdrawalMinAmount: withdrawalMinAmount ?? 0,
	};
};

const getConversionRulesAmount = (
	settings: ISettings,
	fromCurrency: CURRENCYCODETYPE | undefined,
	minNGNAmount: number,
	minGHSAmount: number
) => {
	const isUsd = fromCurrency === 'USD';
	const isNgn = fromCurrency === 'NGN';

	const withdrawalMinFiatAmount = isNgn ? minNGNAmount : minGHSAmount;

	const withdrawalMinAmount = isUsd ? settings.conversionMeta.USD.minimum : withdrawalMinFiatAmount;

	return {
		withdrawalMaxAmount: 0,
		withdrawalMinAmount,
	};
};

export const getConversionWithdrawRules = (settings: ISettings | undefined, fromCurrency: CURRENCYCODETYPE | undefined) => {
	try {
		if (!settings) throw new Error('no settings config found');

		const minNGNAmount = settings.conversionMeta.NGN.minimum;
		const minGHSAmount = settings.conversionMeta.GHS.minimum;

		return getConversionRulesAmount(settings, fromCurrency, minNGNAmount, minGHSAmount);
	} catch (error) {
		console.error(error);
		return {
			withdrawalMaxAmount: 0,
			withdrawalMinAmount: 0,
		};
	}
};

export const getWithdrawRules = (settings: ISettings | undefined, fromCurrency: CURRENCYCODETYPE | undefined) => {
	try {
		if (!settings) throw new Error('no settings config found');

		const minNGNAmount = settings.withdrawalMeta.vip.withdrawal.minima.nigeria;
		const minGHSAmount = settings.withdrawalMeta.vip.withdrawal.minima.ghana;

		return getWithdrawRulesAmount(settings, fromCurrency, minNGNAmount, minGHSAmount);
	} catch (error) {
		console.error(error);
		return {
			withdrawalMaxAmount: 0,
			withdrawalMinAmount: 0,
		};
	}
};

export const getSwapWithdrawRules = (settings: ISettings | undefined, fromCurrency: CURRENCYCODETYPE | undefined) => {
	try {
		if (!settings) throw new Error('no settings config found');

		const minNGNAmount = settings.currencySwapMeta.NGN.minimum;
		const minGHSAmount = settings.currencySwapMeta.GHS.minimum;

		return getWithdrawRulesAmount(settings, fromCurrency, minNGNAmount, minGHSAmount);
	} catch (error) {
		console.error(error);
		return {
			withdrawalMaxAmount: 0,
			withdrawalMinAmount: 0,
		};
	}
};

export const getFiatWithdrawFee = (settings: ISettings | undefined, currency: CURRENCYCODETYPE | undefined) => {
	try {
		if (!settings) throw new Error('no settings config found');

		const isNgn = currency === 'NGN';

		const withdrawalFeeFiatAmount = isNgn
			? settings.withdrawalMeta.vip.withdrawal.fee.nigeria
			: settings.withdrawalMeta.vip.withdrawal.fee.ghana;

		return withdrawalFeeFiatAmount;
	} catch (error) {
		console.error(error);
		return 0;
	}
};

export function formatNumberWithCommas(value: string | number): string {
	const num = typeof value === 'string' ? parseFloat(value) : value;

	if (Number.isNaN(num)) {
		return '0';
	}

	return num.toLocaleString('en-US');
}

export const trimLeadingZero = (value: string): string => {
	if (value === '0') return value;

	if (value.startsWith('0.')) return value;

	return value.replace(/^0+/, '');
};

export const toFixedPrecision = (value: string | number, decimalPlaces = 2): string => {
	const num = typeof value === 'string' ? parseFloat(value) : value;

	if (Number.isNaN(num) || !Number.isFinite(num) || decimalPlaces < 0 || decimalPlaces > 100) {
		return '0.' + '0'.repeat(Math.max(0, Math.min(decimalPlaces, 100)));
	}

	const fixed = num.toFixed(decimalPlaces);

	if (fixed.includes('.')) {
		const [integer, decimal] = fixed.split('.');
		return integer + '.' + decimal.padEnd(decimalPlaces, '0');
	}

	return fixed + '.' + '0'.repeat(decimalPlaces);
};

export const alwaysRefetchApiQueryOptions = {
	refetchOnMountOrArgChange: true,
	refetchOnReconnect: true,
};

/**
 * Format-only PIN rules. Use this for a PIN the user already has — the `oldPin` field and transaction
 * verification — where the strength rules below must NOT apply, otherwise anyone holding a PIN created
 * before those rules existed (e.g. "1111") could never verify a transaction or change their PIN again.
 */
export const pinValidation = yup
	.string()
	.required('PIN is required')
	.matches(/^\d+$/, 'PIN must contain only numbers')
	.length(PINLENGTH, `PIN must be exactly ${PINLENGTH} digits`);

// Straight runs that are too guessable to allow as a new PIN.
const DISALLOWED_PIN_SEQUENCES = ['1234', '4321'];

/**
 * Rules for a PIN being *set* — creating, updating or resetting. Extends {@link pinValidation} (exactly
 * `PINLENGTH` digits, numbers only) with strength checks. Each test short-circuits on an empty value so
 * the `required` message isn't duplicated while the field is still blank.
 */
export const pinStrengthValidation = pinValidation
	.test('no-consecutive-repeat', 'PIN cannot repeat the same digit twice in a row', (value) => !value || !/(\d)\1/.test(value))
	.test(
		'no-sequential-pin',
		'PIN cannot be a simple sequence like 1234',
		(value) => !value || !DISALLOWED_PIN_SEQUENCES.includes(value)
	);

export const capitalizeFirstLetter = (sentence?: string): string => {
	if (!sentence) return '';
	return sentence.charAt(0).toUpperCase() + sentence.slice(1);
};

export const mapArrayFromLength = (arrayLength: number) => Array.from({ length: arrayLength }).map((_, index) => index + 1);

export const PinVerificationSchema = yup.object().shape({
	pin: pinValidation,
});

export const getNumbericValue = (value: string) => Number(value.replace(/,/g, ''));

export const checkNearestValidAmount = (amount: number, currency: CURRENCYCODETYPE | undefined): string => {
	if (currency !== 'NGN' || amount % 100 === 0) return '';

	const nearestValidAmount = Math.round(amount / 100) * 100;

	return `Invalid amount. Please withdraw in multiples of 100s, e.g ${nearestValidAmount.toLocaleString()}.`;
};

export const getNearestValidAmount = (amount: number, isNGN: boolean): number =>
	isNGN ? Math.floor(amount / 100) * 100 : amount;

export const getApiError = (errorDataArg: unknown) => {
	try {
		const errorDataObject = errorDataArg as IApiResponseError | undefined;

		const errorData = errorDataObject?.data;

		if (!errorData) return '';

		const { errors: apiErrors, message } = errorData;

		const apiError = apiErrors?.length ? apiErrors.map((i) => i.message).join(' & ') : undefined;

		if (apiError) return apiError;

		return message === 'invalid cookies passed' ? '' : message;
	} catch (err: unknown) {
		console.error('getApiError: unexpected error shape', err, errorDataArg);

		return '';
	}
};

export const handleApiError = (error: unknown) => {
	const apiError = getApiError(error);

	if (!apiError) return;

	showToast('error', apiError);
};

export const handleApiErrorWithKey = () => (error: unknown) => {
	const apiError = getApiError(error);

	if (!apiError) return;

	showToast('error', apiError);
};

/**
 * Like {@link handleApiError}, but surfaces a generic "Network error" toast when the failure has no
 * response body at all (offline, timeout, empty 5xx). Flows that suppress the global toast via
 * `HIDE_TOAST_KEY` — login and signup — rely solely on this in their catch, so without the fallback a
 * network failure would close the loading dialog with no feedback and look like nothing happened.
 * A body that is present but deliberately blanked by getApiError (e.g. "invalid cookies passed") stays
 * silent, since that's an intentional suppression rather than a missing response.
 */
export const handleApiErrorWithNetworkFallback = (error: unknown) => {
	const apiError = getApiError(error);

	if (apiError) {
		showToast('error', apiError);
		return;
	}

	const hasResponseBody = !!(error as IApiResponseError | undefined)?.data;
	if (!hasResponseBody) showToast('error', 'Network error, please try again');
};

export const selectedBeneficiarySchema = yup.object({
	id: yup.string().required().default(''),
	accountNumber: yup.string().required().default(''),
	accountName: yup.string().required().default(''),
	bankName: yup.string().required().default(''),
});

export const isWithdrawals = (row?: ITransactionHistory<TransactionHistoryType>) => row?.collection === 'withdrawals';

export const isEdits = (row?: ITransactionHistory<TransactionHistoryType>) => row?.collection === 'edits';

export const isConversions = (row?: ITransactionHistory<TransactionHistoryType>) => row?.collection === 'conversions';

export const isSells = (row?: ITransactionHistory<TransactionHistoryType>) => row?.collection === 'sells';

export const isTopups = (row?: ITransactionHistory<TransactionHistoryType>) => row?.collection === 'topups';

export const isApiFulfilledAction = <TPayload = unknown>(action: unknown): action is ApiFulfilledAction<TPayload> => {
	if (
		typeof action === 'object' &&
		action !== null &&
		'type' in action &&
		typeof (action as Record<string, unknown>).type === 'string'
	) {
		const type = (action as Record<string, unknown>).type as string;
		return type.startsWith('api/') && type.endsWith('/fulfilled');
	}
	return false;
};

export const fulfilledWithSettingsMatcher = (action: unknown): action is ApiFulfilledAction<AuthSettingsPayload> =>
	isApiFulfilledAction<AuthSettingsPayload>(action);

export const fulfilledWithBusinessMatcher = (action: unknown): action is ApiFulfilledAction<BusinessesPayload> =>
	isApiFulfilledAction<BusinessesPayload>(action);

export const emptyBeneficiaryState = {
	accountName: '',
	accountNumber: '',
	bankName: '',
	id: '',
};

export const updatePersistor = (partial: Record<string, unknown>) => {
	const current = reduxPersistor.getData('app');
	reduxPersistor.update('app', { ...current, ...partial });
};

export const handleBucketSignedUpload = async ({
	url,
	file,
	contentType,
}: {
	url: string;
	file: File | Blob;
	contentType: string;
}): Promise<UploadQueryReturnValue> => {
	try {
		const res = await fetch(url, {
			method: 'PUT',
			headers: {
				'x-amz-acl': 'public-read',
				'Content-Type': contentType,
			},
			body: file,
		});

		if (!res.ok) {
			return {
				error: {
					status: res.status,
					data: await res.text(),
				} as UploadErrorPayload,
				meta: undefined,
			};
		}

		return { data: undefined, meta: undefined };
	} catch (err) {
		return {
			error: {
				status: 'FETCH_ERROR',
				error: (err as Error).message,
				data: undefined,
			} as UploadErrorPayload,
			meta: undefined,
		};
	}
};

export const formatDisplayString = (value?: string) => (!value || value.toLowerCase().includes('undefined') ? '...' : value);

// Chatwoot serves a chrome-free, embeddable article layout when `show_plain_layout=true` is
// present. Preserve any existing query params (and hash); fall back to the raw string if it
// isn't a parseable URL.
export const buildChatwootEmbedUrl = (articleUrl: string): string => {
	try {
		const url = new URL(articleUrl);
		url.searchParams.set('show_plain_layout', 'true');
		return url.toString();
	} catch {
		return articleUrl;
	}
};

export const viewHelpArticle = (articleId: string) => () => {
	try {
		const chatwootArticleLink = CHATWOOT_ARTICLE_LINKS[articleId];

		if (chatwootArticleLink) {
			import('../../redux')
				.then(({ store, openModal }) => {
					store.dispatch(openModal({ id: 'helpArticle', metadata: { url: chatwootArticleLink } }));
				})
				.catch(console.error);
			return;
		}

		window.open(`https://help.breet.io/en/articles/${articleId}`, '_blank', 'noopener,noreferrer');
	} catch (err) {
		showToast('error', 'Something went wrong');
		console.error(err);
	}
};

export const showNativeNotification = async (title: string, body: string) => {
	const options = {
		body: body,
		icon: '/btLogo.png',
		tag: Date.now().toString(),
		badge: '/btLogo.png',
	};

	if (!('Notification' in window)) {
		console.warn('This browser does not support desktop notifications');
		return;
	}

	let permission = window.Notification.permission;
	if (permission === 'default') {
		permission = await window.Notification.requestPermission();
	}

	if (permission === 'granted' && title) {
		try {
			const registration = await navigator.serviceWorker.ready;

			if ('showNotification' in registration && typeof registration.showNotification === 'function') {
				await registration.showNotification(title, options);
			} else {
				new Notification(title, options);
			}
		} catch (error) {
			console.error('Notification display failed:', error);
		}
	}
};

export const preventFormDefault = (e: FormEvent<HTMLFormElement>) => {
	e.preventDefault();
};

export const scrollToElement = (elementId: string) => {
	const element = document.getElementById(elementId);
	if (element) {
		const isHidden = element.offsetWidth === 0 && element.offsetHeight === 0;
		if (isHidden) {
			const label = document.querySelector(`label[for="${elementId}"]`) as unknown as HTMLElement | undefined;
			if (label) {
				label.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
				return;
			}
		}
		element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
	}
};

type Primitive = string | number | boolean | null | undefined;
type FormValue = Primitive | File | FormValue[] | { [key: string]: FormValue };

// Credential-like fields must never be persisted to storage, even by the form cache.
const SENSITIVE_FORM_KEY = /password|passcode|\bpin\b|otp|\bcvv\b|\bcvc\b|secret|token/i;

export const cleanFormValues = (obj: FormValue): FormValue => {
	if (obj instanceof File) return '';

	if (Array.isArray(obj)) {
		return obj.map(cleanFormValues);
	}

	if (obj && typeof obj === 'object') {
		const keys = Object.keys(obj);
		const isFileObject =
			keys.every((k) => /^\d+$/.test(k)) &&
			Object.values(obj).every(
				(v) => v == null || v instanceof File || (typeof v === 'object' && Object.keys(v as object).length === 0)
			);

		if (isFileObject) return '';

		const result: Record<string, FormValue> = {};
		for (const [key, value] of Object.entries(obj)) {
			if (SENSITIVE_FORM_KEY.test(key)) continue;
			result[key] = cleanFormValues(value);
		}
		return result;
	}

	return obj;
};

export const validateWalletAddress = (address: string, network: string): boolean => {
	if (!address) return false;

	const lowerNetwork = network.toLowerCase();

	// All EVM chains share the same 0x…40-hex address format, so listing more aliases only widens coverage —
	// it can never reject a valid address.
	const evmNetworks = [
		'ethereum',
		'eth',
		'erc20',
		'bsc',
		'binance',
		'bep20',
		'polygon',
		'matic',
		'arbitrum',
		'arb',
		'optimism',
		'op',
		'avalanche',
		'avax',
		'base',
		'fantom',
		'ftm',
	];
	if (evmNetworks.includes(lowerNetwork)) {
		return /^0x[a-fA-F0-9]{40}$/.test(address);
	}

	if (['tron', 'trc20'].includes(lowerNetwork)) {
		return /^T[1-9A-HJ-NP-Za-km-z]{33}$/.test(address);
	}

	if (['solana', 'sol'].includes(lowerNetwork)) {
		return /^[1-9A-HJ-NP-Za-km-z]{32,44}$/.test(address);
	}

	if (['bitcoin', 'btc'].includes(lowerNetwork)) {
		return (
			/^[13][a-km-zA-HJ-NP-Z1-9]{25,34}$/.test(address) ||
			/^bc1[ac-hj-np-zAC-HJ-NP-Z02-9]{11,71}$/.test(address) ||
			/^2[a-km-zA-HJ-NP-Z1-9]{25,34}$/.test(address)
		);
	}

	if (['ton', 'toncoin'].includes(lowerNetwork)) {
		// TON: the 48-char base64url user-friendly form (what users paste), or the raw workchain:hex form.
		return /^[A-Za-z0-9_-]{48}$/.test(address) || /^(?:-1|0):[a-fA-F0-9]{64}$/.test(address);
	}

	return true;
};

export const getNetworkFee = (
	selectedToken: TokenType,
	network: NetworkType,
	usdWithdrawalMeta?: IAuthSettingsUSDWithdrawalMeta
) => {
	try {
		if (!usdWithdrawalMeta) return 0;

		const networkFees = usdWithdrawalMeta.vip.fees[selectedToken];

		return networkFees[network];
	} catch {
		console.error('Network fee not found');
		return 0;
	}
};

export const filterDisabledNetwork =
	(usdWithdrawalMeta: IAuthSettingsUSDWithdrawalMeta, selectedToken: TokenType) =>
	(networkItem: { network: NetworkType; avatar: string }) => {
		try {
			const network = networkItem.network;
			const isNetworkEnabled = usdWithdrawalMeta.vip.networks[network].enabled;
			const tokenNetworks = usdWithdrawalMeta.vip.tokens[selectedToken].networks;
			const isEnabledForToken = network in tokenNetworks ? tokenNetworks[network].enabled : true;
			return isNetworkEnabled && isEnabledForToken;
		} catch (err) {
			console.error(err);
			return false;
		}
	};

export const filterDisabledToken =
	(usdWithdrawalMeta?: IAuthSettingsUSDWithdrawalMeta) => (tokenItem: IWalletInfoOptionProps) => {
		try {
			const token = usdWithdrawalMeta?.vip.tokens[tokenItem.token];
			return token?.enabled;
		} catch (err) {
			console.error(err);
			return false;
		}
	};

export const truncateWithEllipsis = (str: string, start = 9, end = 8) => {
	if (!str) return '';
	if (str.length <= start + end) return str;
	return `${str.substring(0, start)}.....${str.substring(str.length - end)}`;
};

export const convertFxRate = (baseRate: number, valueArg: number, source: 'from' | 'to') => {
	const value = valueArg || 0;
	if (source === 'from') return baseRate * value;

	return value / baseRate;
};

export const cleanRate = (rateString: string): number => {
	if (!rateString) return NaN;

	const cleaned = rateString
		.trim()
		.replace(/[$€£¥₦₵,\s]/g, '')
		.replace(/,/g, '.');

	return Number(cleaned);
};

export const PAGINATION_DOTS = 5.983548;
export const paginationRangeHandler = (start: number, end: number) => {
	const length = end - start + 1;
	/*
		Create an array of certain length and set the elements within it from
		start value to end value.
	*/
	return Array.from({ length }, (_, idx) => idx + start);
};

export const createFormStageContext = <T extends string>() => {
	const ContextComponent = createContext<IUseFormStage<T> | null>(null);

	const useFormStageContext = (): IUseFormStage<T> => {
		const formStageContext = useContext(ContextComponent);

		if (!formStageContext) {
			throw new Error('useFormStageContext must be used within a FormStageContext.Provider');
		}

		return formStageContext;
	};

	return {
		ContextComponent,
		useFormStageContext,
	};
};

export const prohibitedCryptoKeywords = [
	'bitcoin',
	'btc',
	'ethereum',
	'eth',
	'crypto',
	'cryptocurrency',
	'blockchain',
	'defi',
	'nft',
	'altcoin',
	'token',
	'coin',
	'wallet',
	'exchange',
	'trading',
	'mining',
	'hodl',
	'satoshi',
	'litecoin',
	'ltc',
	'ripple',
	'xrp',
	'dogecoin',
	'doge',
	'binance',
	'coinbase',
	'p2p',
	'peer-to-peer',
	'arbitrage',
	'solana',
	'sol',
	'cardano',
	'ada',
	'polkadot',
	'dot',
	'polygon',
	'matic',
	'avalanche',
	'avax',
	'chainlink',
	'link',
	'stellar',
	'xlm',
	'near',
	'optimism',
	'op',
	'arbitrum',
	'arb',
	'tron',
	'trx',
	'toncoin',
	'ton',
	'kaspa',
	'kas',
	'bitcoin cash',
	'bch',
	'shiba inu',
	'shib',
	'uniswap',
	'uni',
	'monero',
	'xmr',
	'cosmos',
	'atom',
	'aptos',
	'apt',
	'sui',
	'pepe',
	'bonk',
	'floki',
	'wif',
	'tether',
	'usdt',
	'usdc',
	'dai',
	'fdusd',
	'tusd',
	'pyusd',
	'usde',
	'cngn',
	'busd',
	'bybit',
	'kucoin',
	'okx',
	'luno',
	'quidax',
	'paxful',
	'remitano',
	'yellowcard',
	'bundle',
	'busha',
	'noones',
	'kraken',
	'gateio',
	'mexc',
	'bitget',
	'exchanger',
	'swap',
	'staking',
	'yield',
	'dex',
	'cex',
	'otc',
	'liquidity',
	'rate',
	'gas fee',
	'airdrop',
	'seed phrase',
	'private key',
	'smart contract',
];

const stripNonAlphanumeric = (input: string) => input.toLowerCase().replace(/[^a-z0-9]/g, '');

// Pre-split the keyword list once. Single-token entries are matched as whole words, so short tickers
// (eth, op, sol, ton…) can't hide inside ordinary words like "method", "operation" or "solar".
// Multi-word entries (e.g. "gas fee") are matched as an exact word sequence.
const singleWordCryptoKeywords = new Set<string>();
const phraseCryptoKeywords: string[] = [];

for (const keyword of prohibitedCryptoKeywords) {
	const parts = keyword.split(/\s+/).map(stripNonAlphanumeric).filter(Boolean);
	if (parts.length === 1) {
		singleWordCryptoKeywords.add(parts[0]);
	} else if (parts.length > 1) {
		phraseCryptoKeywords.push(` ${parts.join(' ')} `);
	}
}

export const narrationValidation = yup
	.string()
	.trim()
	.required('Narration is required')
	.max(NARRATION_MAX, `Narration must be at most ${NARRATION_MAX} characters`)
	.test('no-prohibited-keywords', 'Narration should not include crypto related words', (value) => {
		if (!value) return true;

		// Clean each word on its own — stripping punctuation *within* a word (so obfuscation like
		// "b-t-c" collapses to "btc") without gluing separate words together. This is what stops
		// innocent phrases from forming a keyword across a boundary (e.g. "take this" -> "eth").
		const words = value.toLowerCase().split(/\s+/).map(stripNonAlphanumeric).filter(Boolean);

		if (!words.length) return true;

		if (words.some((word) => singleWordCryptoKeywords.has(word))) return false;

		const paddedNarration = ` ${words.join(' ')} `;

		return !phraseCryptoKeywords.some((phrase) => paddedNarration.includes(phrase));
	});

export const PLATFORMFEEPERCENTLABEL = {
	LESS_THAN_1000: '0 - $999',
	BETWEEN_1000_AND_4999: '$1000 - $4999',
	ABOVE_5000: 'Above $5000',
};

export const isFetchBaseQueryError = (error: unknown): error is FetchBaseQueryError =>
	typeof error === 'object' && error != null && 'status' in error;

export const webhookLogEventTypeObj: Record<IWebhookLogsEventType, string> = {
	'trade.pending': 'Pending Trade',
	'trade.flagged': 'Flagged Trade',
	'trade.completed': 'Completed Trade',
	'withdrawal.pending': 'Pending Withdrawal',
	'withdrawal.processing': 'Processing Withdrawal',
	'withdrawal.completed': 'Completed Withdrawal',
	'withdrawal.rejected': 'Rejected Withdrawal',
	'withdrawal.reversed': 'Reversed Withdrawal',
	'trade.address.created': 'Address Generation',
};

export const getWebhooklogEventType = (type: IWebhookLogsEventType) => webhookLogEventTypeObj[type];

export const webhookLogStatusObj: Record<IWebhookLogsStatusType, IAppStatus> = {
	delivered: 'successful',
	failed: 'unsuccessful',
};

export const getWebhooklogStatus = (status: IWebhookLogsStatusType) => webhookLogStatusObj[status];

export const cryptoSettingsOptions = [
	{
		currency: 'ngn',
		description: 'Your crypto deposits will be automatically converted to Naira and deposited to your Naira wallet.',
	},
	{
		currency: 'ghs',
		description: 'Your crypto deposits will be automatically converted to Cedis and deposited to your Cedis wallet.',
	},
	{
		currency: 'usd',
		description: 'The USD equivalent of your crypto deposits will be deposited to your USD wallet.',
	},
] as const;

export const getFxConversionType = (fromCurrencyCode: CURRENCYCODETYPE, toCurrencyCode: CURRENCYCODETYPE): FXCONVERSION_TYPE => {
	switch (fromCurrencyCode) {
		case 'USD':
			if (toCurrencyCode === 'NGN') {
				return FXCONVERSION.USDToNGN;
			}
			return FXCONVERSION.USDToGHS;
		case 'NGN':
			if (toCurrencyCode === 'GHS') {
				return FXCONVERSION.NGNToGHS;
			}
			return FXCONVERSION.FIATToUSD;
		case 'GHS':
			if (toCurrencyCode === 'NGN') {
				return FXCONVERSION.GHSToNGN;
			}
			return FXCONVERSION.FIATToUSD;
		default:
			return FXCONVERSION.FIATToUSD;
	}
};

export const getSafeObjectField = <T extends object, K extends keyof T, D = undefined>(
	obj: T | undefined | null,
	path: K,
	defaultValue?: D
): T[K] | D => {
	const value = obj && path in obj ? obj[path] : undefined;
	return (value ?? defaultValue) as T[K] | D;
};

export const convertCryptoAmount = (value: string | number | null | undefined) => {
	if (value === null || value === undefined || value === '') return '—';
	const num = Number(value);
	if (Number.isNaN(num)) return '—';
	return num.toLocaleString('en-US', {
		minimumFractionDigits: 0,
		maximumFractionDigits: 20,
	});
};

export const convertCryptoInputAmount = (value: string | number | null | undefined) => {
	if (value === null || value === undefined || value === '') return '—';
	const num = Number(value);
	if (Number.isNaN(num)) return '0';
	return num.toLocaleString('en-US', {
		minimumFractionDigits: 0,
		maximumFractionDigits: 7,
	});
};

export const NewDeviceLoginSchema = yup.object({
	otp: otpValidation,
	recoveryCode: yup
		.string()
		.notRequired()
		.test('recovery-code-digits', 'Enter a 10-character alphanumeric recovery code', (value) => {
			if (value == null || value === '') return true;
			return /^[A-Za-z0-9]{10}$/.test(value.trim());
		}),
});

export const onFormPreventDefault = (e: FormEvent<HTMLFormElement>) => e.preventDefault();

export const getTeamMemberRoleLabel = (role: ITeamMembersRole) => {
	switch (role) {
		case 'owner':
			return 'Owner';
		case 'super_admin':
			return 'Super Admin';
		case 'subadmin':
			return 'Sub-Admin';
		case 'developer':
			return 'Developer';
		case 'customer_support':
			return 'Customer Support';
		default:
			return '';
	}
};

export const passwordValidation = yup
	.string()
	.required('Password is required')
	.max(32, 'Password must be at most 32 characters')
	.matches(
		/(?=.*[A-Z])(?=.*[a-z])(?=.*\d).{8,}$/,
		'Password must contain at least 8 characters, 1 uppercase, 1 lowercase, and 1 number.'
	);

export const loginPasswordRules = [
	{ label: 'Minimum of 8 characters', pattern: new RegExp(/.{8,}$/) },
	{ label: 'At least 1 uppercase', pattern: new RegExp(/.*[A-Z]/) },
	{ label: 'At least 1 lowercase', pattern: new RegExp(/[a-z]/) },
	{ label: 'At least 1 number', pattern: new RegExp(/.*\d/) },
];

export const LoginSchema = yup.object({
	email: emailValidation,
	password: yup.string().max(32, 'Password should be less than or equal to 32 characters').required('Password is required'),
});

export type LoginSchemaType = yup.InferType<typeof LoginSchema>;

export const toISODate = (dateStr: string): string => {
	const date = new Date(dateStr);
	return Number.isNaN(date.getTime()) ? '' : date.toISOString();
};

export const toExportEndISODate = (dateStr: string): string => {
	const date = new Date(dateStr);
	if (Number.isNaN(date.getTime())) return '';

	date.setUTCDate(date.getUTCDate() + 1);
	return date.toISOString();
};

export const downloadBlob = (blob: Blob, filename: string) => {
	const url = URL.createObjectURL(blob);

	const a = document.createElement('a');
	a.href = url;
	a.download = filename;

	document.body.appendChild(a);
	a.click();
	document.body.removeChild(a);

	URL.revokeObjectURL(url);
};

export const downloadQrCode = (dataUrl: string, filename = 'qr-code.png') => {
	if (!dataUrl) return;

	const a = document.createElement('a');
	a.href = dataUrl;
	a.download = filename;

	document.body.appendChild(a);
	a.click();
	document.body.removeChild(a);
};

export const parseDeviceInfo = (deviceStr: string | null | undefined): DeviceInfo => {
	const result: DeviceInfo = {
		deviceName: 'Unknown',
		deviceModel: 'Generic',
	};

	if (!deviceStr) return result;
	const lowerStr = deviceStr.toLowerCase();

	if (lowerStr.includes('iphone')) {
		result.deviceName = 'Apple iPhone';

		if (lowerStr.includes('13 pro max') || lowerStr.includes('iphone14,3')) {
			result.deviceModel = '13 Pro Max';
		} else if (lowerStr.includes('14 pro max') || lowerStr.includes('iphone15,3')) {
			result.deviceModel = '14 Pro Max';
		} else if (lowerStr.includes('15 pro max') || lowerStr.includes('iphone16,2')) {
			result.deviceModel = '15 Pro Max';
		} else if (lowerStr.includes('16 pro max')) {
			result.deviceModel = '16 Pro Max';
		}
	} else if (lowerStr.includes('ipad')) {
		result.deviceName = 'Apple iPad';
	} else if (lowerStr.includes('macintosh') || lowerStr.includes('mac os')) {
		result.deviceName = 'Apple Mac';
	} else if (lowerStr.includes('android')) {
		result.deviceName = 'Android Device';
	} else if (lowerStr.includes('windows')) {
		result.deviceName = 'Windows PC';
	}

	return result;
};

export const extractComputerModel = (deviceStr: string | null | undefined): DeviceModel => {
	if (!deviceStr) {
		return 'unknown';
	}

	const lowerStr = deviceStr.toLowerCase();

	const modelMap: Record<Exclude<DeviceModel, 'unknown'>, string[]> = {
		iphone: ['iphone', 'iphone os'],
		ipad: ['ipad'],
		apple: ['macintosh', 'mac os', 'apple', 'macbook'],
		windows: ['windows', 'win64', 'win32'],
		android: ['android'],
		linux: ['linux', 'ubuntu', 'debian'],
	};

	for (const [model, keywords] of Object.entries(modelMap)) {
		if (keywords.some((keyword) => lowerStr.includes(keyword))) {
			return model as DeviceModel;
		}
	}

	return 'unknown';
};

export const urlValidation = (optional: boolean) =>
	yup
		.string()
		.trim()
		.max(URL_MAX, `URL should be less than or equal to ${URL_MAX} characters`)
		.test('is-valid-url-format', 'url must start with https:// or www. and contain a valid domain extension', (value) => {
			if (!value) return optional;

			const hasHttps = value.startsWith('https://');
			const hasWww = value.startsWith('www.');

			if (!hasHttps && !hasWww) {
				return false;
			}

			const urlToParse = hasWww ? `https://${value}` : value;

			try {
				const parsedUrl = new URL(urlToParse);
				const hostParts = parsedUrl.hostname.split('.');

				return hostParts.length >= 2 && hostParts[hostParts.length - 1].length >= 2;
			} catch {
				return false;
			}
		});

export const businessDescriptionValidation = yup
	.string()
	.transform((value?: string) => {
		if (!value) return value;
		return value.replace(/[\r\n]+/g, ' ');
	})
	.trim()
	.min(BUSINESS_DESCRIPTION_MIN, `Business description must be at least ${BUSINESS_DESCRIPTION_MIN} characters`)
	.max(BUSINESS_DESCRIPTION_MAX, `Business description must be at most ${BUSINESS_DESCRIPTION_MAX} characters`)
	.required('Business description is required');

export const filterPageMenuItemPermissions =
	(
		isKybPending: boolean,
		hasAnyPermission: (requiredPermissions: ITemMemberPermission[]) => boolean,
		hasPermission: (permission: ITemMemberPermission) => boolean,
		userData?: IUser
	) =>
	(i: IPageMenuItem) => {
		if (i.hide) return false;

		if (i.requiresKyb && isKybPending) return false;

		if (i.permissions) {
			const allowed = Array.isArray(i.permissions) ? hasAnyPermission(i.permissions) : hasPermission(i.permissions);
			if (!allowed) return false;
		}

		if (i.role) {
			const allowedRoles = Array.isArray(i.role) ? i.role : [i.role];
			return userData?.businessRole ? allowedRoles.includes(userData.businessRole) : false;
		}

		return true;
	};
